import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, TrendingUp, TrendingDown, 
  Plus, Search, BarChart3,
  Wallet, PiggyBank, Receipt, Trash2,
  CheckCircle, Clock, AlertTriangle, X,
  Building2, FileText, ShoppingCart, Tag, Edit2,
  Package, Truck, Image, Settings, Users,
  ClipboardList, UserCheck, Boxes, ListChecks,
  ChevronDown, ChevronRight, ChevronsLeft, ChevronsRight,
  PackageSearch, Calculator, Landmark, LayoutDashboard,
  RefreshCw, Layers, AlertCircle,
  Handshake, Undo2, Paperclip, FileDown
} from 'lucide-react';
import {
  FonteModal, PatrocinioModal,
  SecaoFontes, SecaoPatrocinios, SecaoReembolsos, PainelAnual, MoedasConfig,
} from './Patrocinios';
import {
  Fonte, SaldoFonte, Patrocinio, Contrapartida, Anexo,
  ResumoAno, FinanceConfig, fmt, fmtData, uploadAnexo, linkAnexo,
} from './tipos';
import { CobrancaModal, PayloadCobranca, ParteCobranca } from './Cobranca';
import { DetalheLancamento } from './DetalheLancamento';

interface UserSession {
  usuario: { id: string; nome: string; email: string; role: string };
  empresa?: { id: string; nome: string; logo?: string };
}

interface ContaFinanceira {
  _id: string;
  tipo: 'pagar' | 'receber';
  descricao: string;
  valor: number;
  vencimento: string;
  categoria: string;
  fornecedor?: string;
  cliente?: string;
  status: 'pendente' | 'pago' | 'recebido' | 'vencido';
  observacoes?: string;
  criadoPor: string;
  createdAt: string;
  // ─── multimoeda + rastreio da origem do dinheiro ───
  moeda?: string;
  cotacao?: number;
  valorBase?: number;
  fonteId?: string;
  fonteNome?: string;
  reembolsavel?: boolean;
  reembolsado?: boolean;
  dataPagamento?: string;
  dataRecebimento?: string;
  patrocinioId?: string;
  anexos?: Anexo[];
}

interface DashboardData {
  pagar: { pendente: number; pago: number };
  receber: { pendente: number; recebido: number };
  saldo: number;
  saldo_previsto: number;
  moedaBase?: string;
  /** Moeda em que o backend devolveu os totais (a do seletor do painel). */
  moeda?: string;
  cambio?: { fonte: string | null; atualizadoEm: string | null; aviso?: string };
  categorias: Array<{ _id: string; total: number; quantidade: number }>;
}

interface Categoria {
  _id: string;
  nome: string;
  tipo: string;
  cor: string;
  createdAt: string;
}

interface CentroCusto {
  _id: string;
  nome: string;
  codigo: string;
  descricao: string;
  responsavel: string;
  ativo: boolean;
  createdAt: string;
}

interface PlanoContas {
  _id: string;
  codigo: string;
  nome: string;
  tipo: string;
  natureza: string;
  contaPai: string;
  descricao: string;
  ativo: boolean;
  createdAt: string;
}

interface Compra {
  _id: string;
  numero: number;
  fornecedor: string;
  descricao: string;
  itens: Array<{ descricao: string; quantidade: number; valorUnitario: number; total: number }>;
  valorTotal: number;
  dataCompra: string;
  dataEntrega: string;
  status: string;
  formaPagamento: string;
  observacoes: string;
  centroCusto: string;
  criadoPor: string;
  createdAt: string;
}

interface Fornecedor {
  _id: string;
  razaoSocial: string;
  nomeFantasia: string;
  cnpj: string;
  email: string;
  telefone: string;
  endereco: string;
  cidade: string;
  estado: string;
  contato: string;
  observacoes: string;
  ativo: boolean;
  createdAt: string;
}

interface Comprador {
  _id: string;
  nome: string;
  email: string;
  departamento: string;
  limiteAprovacao: number;
  ativo: boolean;
  createdAt: string;
}

interface Produto {
  _id: string;
  codigo: string;
  nome: string;
  categoria: string;
  unidade: string;
  precoCompra: number;
  precoVenda: number;
  descricao: string;
  ativo: boolean;
  createdAt: string;
}

interface ItemEstoque {
  _id: string;
  codigo: string;
  nome: string;
  categoria: string;
  unidade: string;
  quantidadeAtual: number;
  quantidadeMinima: number;
  localizacao: string;
  custoMedio: number;
  ativo: boolean;
  createdAt: string;
}

interface Inventario {
  _id: string;
  numero: number;
  descricao: string;
  dataInicio: string;
  dataFim: string;
  status: string;
  responsavel: string;
  itens: Array<{ itemId: string; nomeItem: string; quantidadeSistema: number; quantidadeContada: number; diferenca: number }>;
  observacoes: string;
  createdAt: string;
}

interface Orcamento {
  _id: string;
  categoria: string;
  centroCusto: string;
  periodo: string;
  ano: number;
  mes: number;
  valorOrcado: number;
  valorRealizado: number;
  observacoes: string;
  criadoPor: string;
  createdAt: string;
}

// ── Orçamento vindo do GIX (arqocpg), somente leitura ────────────────────────
// O ERP mantém DOIS orçamentos paralelos que não se somam entre si:
//   grupo    -> orçamento do grupo de empresa (hoje é o que está vivo)
//   empresa  -> orçamento de uma empresa isolada
// Como os tipos de despesa dos dois são conjuntos disjuntos, 'consolidado'
// (grupo + empresas) é uma soma válida e não duplica valor.
interface OrcamentoGixItem {
  codigo: number;
  despesa: string;
  grupo_despesa: string;
  meses: Record<string, number>;   // { 'YYYY-MM': valor } — orçado
  total: number;                   // orçado no período
  realizado_meses?: Record<string, number>;   // títulos a pagar, por vencimento
  realizado_total?: number | null;
  titulos?: number;
  pct_consumido?: number | null;
  oc_meses?: Record<string, number>;          // OC em aberto LIBERADA
  oc_total?: number;
  ocs?: number;
  oc_nlib_meses?: Record<string, number>;     // OC em aberto NÃO liberada
  oc_nlib_total?: number;
}

interface OrcamentoGix {
  status: string;
  escopo: { tipo: 'grupo' | 'empresa' | 'consolidado'; codigo: number | null };
  periodo: { de: string; ate: string };
  competencias: string[];
  itens: OrcamentoGixItem[];
  total_geral: number;
  total_tipos_despesa: number;
  realizado_total_geral?: number;
  realizado_fora_orcamento?: number;
  realizado_fonte?: string;
  oc_aberto?: boolean;
  oc_total_geral?: number;
  oc_nlib_total_geral?: number;
}

interface OrcamentoGixEscopos {
  grupos: Array<{ grupo: number; nome: string; lancamentos: number; periodo_inicial: string; periodo_final: string }>;
  empresas: Array<{ empresa: number; lancamentos: number; periodo_inicial: string; periodo_final: string }>;
}

interface ContaBancaria {
  _id: string;
  banco: string;
  agencia: string;
  conta: string;
  tipo: 'corrente' | 'poupanca' | 'investimento' | 'caixa';
  apelido: string;
  saldoInicial: number;
  saldoAtual: number;
  ativo: boolean;
  observacoes: string;
  createdAt: string;
}

/** Dimensões do relatório cubo — espelha DIMENSOES_CUBO do backend. */
const DIMENSOES_CUBO = [
  { valor: 'categoria', rotulo: 'Categoria' },
  { valor: 'contraparte', rotulo: 'Fornecedor / Cliente' },
  { valor: 'fonte', rotulo: 'Caixa / fonte' },
  { valor: 'status', rotulo: 'Situação' },
  { valor: 'moeda', rotulo: 'Moeda' },
  { valor: 'tipo', rotulo: 'Pagar / Receber' },
  { valor: 'mes', rotulo: 'Mês' },
  { valor: 'ano', rotulo: 'Ano' },
  { valor: 'nenhuma', rotulo: 'Nenhuma (só total)' },
];

type Section = 'dashboard' | 'contas-pagar' | 'contas-receber' | 'cobrancas' | 'relatorios'
  | 'orcamentos' | 'caixas-bancos'
  | 'patrocinios' | 'fontes' | 'reembolsos'
  | 'categorias' | 'centros-custo' | 'plano-contas'
  | 'compras' | 'fornecedores' | 'compradores' | 'produtos'
  | 'estoque' | 'inventario' | 'configuracoes';

interface FinanceAppProps {
  session: UserSession;
  onVoltar: () => void;
}

const FinanceApp: React.FC<FinanceAppProps> = ({ session, onVoltar }) => {
  const [currentSection, setCurrentSection] = useState<Section>('dashboard');
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [contasPagar, setContasPagar] = useState<ContaFinanceira[]>([]);
  const [contasReceber, setContasReceber] = useState<ContaFinanceira[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [centrosCusto, setCentrosCusto] = useState<CentroCusto[]>([]);
  const [planoContas, setPlanoContas] = useState<PlanoContas[]>([]);
  const [compras, setCompras] = useState<Compra[]>([]);
  const [fornecedores, setFornecedores] = useState<Fornecedor[]>([]);
  const [compradores, setCompradores] = useState<Comprador[]>([]);
  const [itensEstoque, setItensEstoque] = useState<ItemEstoque[]>([]);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [inventarios, setInventarios] = useState<Inventario[]>([]);
  const [orcamentos, setOrcamentos] = useState<Orcamento[]>([]);
  const [contasBancarias, setContasBancarias] = useState<ContaBancaria[]>([]);

  // ── Orçamento do GIX ──────────────────────────────────────────────────────
  const [orcFonte, setOrcFonte] = useState<'gix' | 'manual'>('gix');
  const [orcGix, setOrcGix] = useState<OrcamentoGix | null>(null);
  const [orcEscopos, setOrcEscopos] = useState<OrcamentoGixEscopos | null>(null);
  const [orcTipo, setOrcTipo] = useState<'grupo' | 'empresa' | 'consolidado'>('grupo');
  const [orcCodigo, setOrcCodigo] = useState<number | null>(null);
  const [orcAno, setOrcAno] = useState<number>(new Date().getFullYear());
  const [orcZerados, setOrcZerados] = useState(true);
  const [orcBusca, setOrcBusca] = useState('');
  const [orcDrill, setOrcDrill] = useState<
    { tipo: 'titulos' | 'oc'; codigo: number; despesa: string; competencia?: string } | null>(null);
  const [orcDrillData, setOrcDrillData] = useState<any | null>(null);
  const [orcDrillLoading, setOrcDrillLoading] = useState(false);
  const [orcTitulos, setOrcTitulos] = useState(true);
  const [orcOcAberto, setOrcOcAberto] = useState(false);
  const [orcOcSoLiberadas, setOrcOcSoLiberadas] = useState(true);
  const [orcLoading, setOrcLoading] = useState(false);
  const [orcErro, setOrcErro] = useState<string | null>(null);
  const [_loading, setLoading] = useState(true);
  // ─── painel: ano e moeda de exibição ───
  // O ano era fixo no ano corrente e a moeda era sempre a base da empresa.
  const [anoPainel, setAnoPainel] = useState<number>(new Date().getFullYear());
  const [moedaPainel, setMoedaPainel] = useState<string>('');   // '' = moeda base
  // ─── cobrança (invoice / nota de débito) ───
  const [cobrando, setCobrando] = useState<ContaFinanceira | null>(null);
  const [clientesCobranca, setClientesCobranca] = useState<ParteCobranca[]>([]);
  const [cobrancas, setCobrancas] = useState<any[]>([]);
  // ─── relatório cubo ───
  const [cuboLinhas, setCuboLinhas] = useState('categoria');
  const [cuboColunas, setCuboColunas] = useState('mes');
  const [cuboTipo, setCuboTipo] = useState('pagar');
  const [cuboStatus, setCuboStatus] = useState('');
  const [cuboData, setCuboData] = useState<any | null>(null);
  const [cuboCarregando, setCuboCarregando] = useState(false);
  // ─── multimoeda, fontes, contratos ───
  const [config, setConfig] = useState<FinanceConfig>({ moedaBase: 'BRL', moedas: ['BRL'], cotacoes: {} });
  const [fontes, setFontes] = useState<Fonte[]>([]);
  const [saldosFontes, setSaldosFontes] = useState<SaldoFonte[]>([]);
  const [patrocinios, setPatrocinios] = useState<Patrocinio[]>([]);
  const [resumoAno, setResumoAno] = useState<ResumoAno | null>(null);
  const [reembolsos, setReembolsos] = useState<any[]>([]);
  const [reembolsosResumo, setReembolsosResumo] = useState<{ totalBase: number; quantidade: number; porFonte: any[] }>(
    { totalBase: 0, quantidade: 0, porFonte: [] }
  );
  const [filtroReembolso, setFiltroReembolso] = useState('pendente');
  const [fonteEditando, setFonteEditando] = useState<Fonte | null>(null);
  const [patrocinioEditando, setPatrocinioEditando] = useState<Patrocinio | null>(null);

  const [showNovaConta, setShowNovaConta] = useState<'pagar' | 'receber' | null>(null);
  // título que está recebendo baixa — o modal pergunta por qual caixa/banco o dinheiro andou
  const [baixando, setBaixando] = useState<ContaFinanceira | null>(null);
  const [detalhando, setDetalhando] = useState<ContaFinanceira | null>(null);
  const [showModal, setShowModal] = useState<string | null>(null);
  const [filtroStatus, setFiltroStatus] = useState<string>('todos');
  const [busca, setBusca] = useState('');
  const [menuOpen, setMenuOpen] = useState(true);
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({ 'FINANCEIRO': true, 'PATROCÍNIO': true, 'COMPRAS': true, 'ESTOQUE': true, 'CADASTROS': true });
  const MODULOS_PADRAO: Record<string, Record<string, boolean>> = {
    'Financeiro': { 'Dashboard': true, 'Contas a Pagar': true, 'Contas a Receber': true, 'Cobranças': true, 'Relatórios': true, 'Orçamento': true, 'Caixas e Bancos': true },
    'Contratos': { 'Contratos': true, 'Fontes de Recurso': true, 'Reembolsos': true },
    'Compras': { 'Ordens de Compra': true, 'Fornecedores': true, 'Compradores': true },
    'Estoque': { 'Produtos': true, 'Controle de Estoque': true, 'Inventário': true },
    'Cadastros': { 'Categorias': true, 'Centro de Custos': true, 'Plano de Contas': true },
  };
  const [modulesConfig, setModulesConfig] = useState<Record<string, Record<string, boolean>>>(MODULOS_PADRAO);
  const persistirModulos = (cfg: Record<string, Record<string, boolean>>) => {
    try { localStorage.setItem(`erp_modulos_${empresaId}`, JSON.stringify(cfg)); } catch { /* modo privado */ }
    return cfg;
  };
  const toggleModule = (modName: string) => {
    setModulesConfig(prev => {
      const features = prev[modName];
      const allOn = Object.values(features).every(v => v);
      const updated: Record<string, boolean> = {};
      for (const k of Object.keys(features)) updated[k] = !allOn;
      return persistirModulos({ ...prev, [modName]: updated });
    });
  };
  const toggleFeature = (modName: string, featName: string) => {
    setModulesConfig(prev => persistirModulos({
      ...prev,
      [modName]: { ...prev[modName], [featName]: !prev[modName][featName] }
    }));
  };

  const toggleSection = (label: string) => {
    setExpandedSections(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const empresaId = session.empresa?.id || '';

  /**
   * Toda chamada ao financeiro vai assinada com o e-mail da sessão.
   * O servidor resolve a empresa a partir dele NO BANCO e ignora
   * qualquer empresaId que venha na URL — sem isso, trocar o id no
   * endereço daria acesso ao financeiro de outra empresa.
   */
  const financeFetch = (url: string, init: RequestInit = {}) => fetch(url, {
    ...init,
    headers: {
      ...(init.headers || {}),
      'X-User-Email': session.usuario.email || '',
    },
  });

  useEffect(() => {
    if (empresaId) {
      carregarDashboard();
      carregarContas();
      carregarCategorias();
      carregarCentrosCusto();
      carregarPlanoContas();
      carregarCompras();
      carregarFornecedores();
      carregarCompradores();
      carregarEstoque();
      carregarInventarios();
      carregarProdutos();
      carregarOrcamentos();
      carregarEscoposGix();
      carregarContasBancarias();
      carregarConfig();
      carregarFontes();
      carregarPatrocinios();
      carregarResumoAno();
      carregarReembolsos();
      carregarClientesCobranca();
      carregarCobrancas();
    }
  }, [empresaId]);

  // Trocar o ano ou a moeda do painel recarrega só o que depende deles
  useEffect(() => {
    if (!empresaId) return;
    carregarResumoAno();
    carregarDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anoPainel, moedaPainel]);

  // O cubo refaz a conta ao mexer em qualquer seletor dele
  useEffect(() => {
    if (empresaId && currentSection === 'relatorios') carregarCubo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSection, cuboLinhas, cuboColunas, cuboTipo, cuboStatus, anoPainel, moedaPainel]);

  // Reembolsos recarregam ao trocar o filtro da própria tela
  useEffect(() => {
    if (empresaId) carregarReembolsos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroReembolso]);

  // Carregar logo salva
  useEffect(() => {
    const saved = localStorage.getItem(`erp_logo_${empresaId}`);
    if (saved) setLogoUrl(saved);
  }, [empresaId]);

  // Quais módulos ficam visíveis é escolha de cada empresa.
  useEffect(() => {
    if (!empresaId) return;
    try {
      const salvo = localStorage.getItem(`erp_modulos_${empresaId}`);
      if (!salvo) { setModulesConfig(MODULOS_PADRAO); return; }
      const parsed = JSON.parse(salvo) as Record<string, Record<string, boolean>>;
      // mescla com o padrão: módulo novo aparece ligado em quem já tinha config salva
      const mesclado: Record<string, Record<string, boolean>> = {};
      for (const [mod, feats] of Object.entries(MODULOS_PADRAO)) {
        mesclado[mod] = { ...feats, ...(parsed[mod] || {}) };
      }
      setModulesConfig(mesclado);
    } catch {
      setModulesConfig(MODULOS_PADRAO);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId]);

  // Recarrega a matriz do GIX quando o escopo/período muda (só na aba Orçamento)
  useEffect(() => {
    if (currentSection === 'orcamentos' && orcFonte === 'gix') carregarOrcamentoGix();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSection, orcFonte, orcTipo, orcCodigo, orcAno, orcZerados, orcTitulos, orcOcAberto, orcOcSoLiberadas]);

  const carregarDashboard = async () => {
    try {
      // ano e moeda do seletor valem para o painel inteiro
      const res = await financeFetch(
        `/api/finance/dashboard?empresaId=${encodeURIComponent(empresaId)}&ano=${anoPainel}` +
        `${moedaPainel ? `&moeda=${moedaPainel}` : ''}`);
      if (res.ok) {
        const data = await res.json();
        setDashboardData(data.data);
      }
    } catch (e) {
      console.error('Erro ao carregar dashboard:', e);
    }
  };

  const carregarContas = async () => {
    setLoading(true);
    try {
      const [resPagar, resReceber] = await Promise.all([
        financeFetch(`/api/finance/contas-pagar?empresaId=${encodeURIComponent(empresaId)}`),
        financeFetch(`/api/finance/contas-receber?empresaId=${encodeURIComponent(empresaId)}`)
      ]);
      if (resPagar.ok) { const data = await resPagar.json(); setContasPagar(data.data || []); }
      if (resReceber.ok) { const data = await resReceber.json(); setContasReceber(data.data || []); }
    } catch (e) {
      console.error('Erro ao carregar contas:', e);
    } finally {
      setLoading(false);
    }
  };

  const carregarCategorias = async () => {
    try {
      const res = await financeFetch(`/api/finance/categorias?empresaId=${encodeURIComponent(empresaId)}`);
      if (res.ok) { const data = await res.json(); setCategorias(data.data || []); }
    } catch (e) { console.error('Erro categorias:', e); }
  };

  const carregarCentrosCusto = async () => {
    try {
      const res = await financeFetch(`/api/finance/centros-custo?empresaId=${encodeURIComponent(empresaId)}`);
      if (res.ok) { const data = await res.json(); setCentrosCusto(data.data || []); }
    } catch (e) { console.error('Erro centros custo:', e); }
  };

  const carregarPlanoContas = async () => {
    try {
      const res = await financeFetch(`/api/finance/plano-contas?empresaId=${encodeURIComponent(empresaId)}`);
      if (res.ok) { const data = await res.json(); setPlanoContas(data.data || []); }
    } catch (e) { console.error('Erro plano contas:', e); }
  };

  const carregarCompras = async () => {
    try {
      const res = await financeFetch(`/api/finance/compras?empresaId=${encodeURIComponent(empresaId)}`);
      if (res.ok) { const data = await res.json(); setCompras(data.data || []); }
    } catch (e) { console.error('Erro compras:', e); }
  };

  const carregarFornecedores = async () => {
    try {
      const res = await financeFetch(`/api/finance/fornecedores?empresaId=${encodeURIComponent(empresaId)}`);
      if (res.ok) { const data = await res.json(); setFornecedores(data.data || []); }
    } catch (e) { console.error('Erro fornecedores:', e); }
  };

  const carregarCompradores = async () => {
    try {
      const res = await financeFetch(`/api/finance/compradores?empresaId=${encodeURIComponent(empresaId)}`);
      if (res.ok) { const data = await res.json(); setCompradores(data.data || []); }
    } catch (e) { console.error('Erro compradores:', e); }
  };

  const carregarEstoque = async () => {
    try {
      const res = await financeFetch(`/api/finance/estoque?empresaId=${encodeURIComponent(empresaId)}`);
      if (res.ok) { const data = await res.json(); setItensEstoque(data.data || []); }
    } catch (e) { console.error('Erro estoque:', e); }
  };

  const carregarInventarios = async () => {
    try {
      const res = await financeFetch(`/api/finance/inventarios?empresaId=${encodeURIComponent(empresaId)}`);
      if (res.ok) { const data = await res.json(); setInventarios(data.data || []); }
    } catch (e) { console.error('Erro inventarios:', e); }
  };

  const carregarProdutos = async () => {
    try {
      const res = await financeFetch(`/api/finance/produtos?empresaId=${encodeURIComponent(empresaId)}`);
      if (res.ok) { const data = await res.json(); setProdutos(data.data || []); }
    } catch (e) { console.error('Erro produtos:', e); }
  };

  // Descobre quais grupos/empresas têm orçamento lançado no GIX e já escolhe
  // o primeiro grupo como escopo inicial (é onde o orçamento vivo está hoje).
  const carregarEscoposGix = async () => {
    try {
      const res = await fetch('/api/gix/orcamento/grupos');
      if (!res.ok) return;
      const data = await res.json();
      if (data?.status !== 'ok') return;
      setOrcEscopos({ grupos: data.grupos || [], empresas: data.empresas || [] });
      setOrcCodigo(prev => prev ?? (data.grupos?.[0]?.grupo ?? null));
    } catch (e) {
      console.error('Erro escopos orçamento GIX:', e);
    }
  };

  const carregarOrcamentoGix = async () => {
    if (orcTipo !== 'consolidado' && orcCodigo == null) return;
    setOrcLoading(true);
    setOrcErro(null);
    try {
      const p = new URLSearchParams({ ano: String(orcAno) });
      if (orcTipo === 'consolidado') p.set('consolidado', '1');
      else p.set(orcTipo, String(orcCodigo));
      if (orcZerados) p.set('zerados', '1');
      if (!orcTitulos) p.set('titulos', '0');
      if (orcOcAberto) p.set('oc_aberto', '1');

      const res = await fetch(`/api/gix/orcamento?${p.toString()}`);
      const data = await res.json().catch(() => null);
      if (!res.ok || data?.status !== 'ok') {
        setOrcGix(null);
        // 500/502/504 sem corpo JSON = o proxy não conseguiu falar com a api_gix
        // (ela não está no ar). 503 = a api_gix respondeu, mas o banco do GIX caiu.
        const semServico = [500, 502, 504].includes(res.status) && !data?.message;
        setOrcErro(
          semServico
            ? 'A api_gix não está respondendo na porta 5002. Inicie com "python api_gix.py" e tente de novo.'
            : res.status === 503
              ? 'A api_gix está no ar, mas sem conexão com o banco do GIX.'
              : (data?.message || `Falha ao consultar o GIX (HTTP ${res.status}).`)
        );
        return;
      }
      setOrcGix(data);
    } catch (e) {
      console.error('Erro orçamento GIX:', e);
      setOrcGix(null);
      setOrcErro('Não foi possível falar com a api_gix.');
    } finally {
      setOrcLoading(false);
    }
  };

  // Drill-down: busca os documentos por trás de um número da grade
  const abrirDrill = async (
    tipo: 'titulos' | 'oc', codigo: number, despesa: string, competencia?: string
  ) => {
    setOrcDrill({ tipo, codigo, despesa, competencia });
    setOrcDrillData(null);
    setOrcDrillLoading(true);
    try {
      const p = new URLSearchParams({ tipo, codigo: String(codigo) });
      if (competencia) p.set('competencia', competencia);
      else p.set('ano', String(orcAno));
      if (orcTipo === 'grupo' && orcCodigo != null) p.set('grupo', String(orcCodigo));
      else if (orcTipo === 'empresa' && orcCodigo != null) p.set('empresa', String(orcCodigo));
      const res = await fetch(`/api/gix/orcamento/detalhe?${p.toString()}`);
      const data = await res.json().catch(() => null);
      setOrcDrillData(res.ok && data?.status === 'ok' ? data : { erro: data?.message || 'Falha ao consultar' });
    } catch {
      setOrcDrillData({ erro: 'Não foi possível falar com a api_gix.' });
    } finally {
      setOrcDrillLoading(false);
    }
  };

  const carregarOrcamentos = async () => {
    try {
      const res = await financeFetch(`/api/finance/orcamentos?empresaId=${encodeURIComponent(empresaId)}`);
      if (res.ok) { const data = await res.json(); setOrcamentos(data.data || []); }
    } catch (e) { console.error('Erro orcamentos:', e); }
  };

  const carregarContasBancarias = async () => {
    try {
      const res = await financeFetch(`/api/finance/contas-bancarias?empresaId=${encodeURIComponent(empresaId)}`);
      if (res.ok) { const data = await res.json(); setContasBancarias(data.data || []); }
    } catch (e) { console.error('Erro contas bancarias:', e); }
  };

  /* ═══ Multimoeda / fontes / contratos ═══ */

  const qs = () => `empresaId=${encodeURIComponent(empresaId)}`;

  const carregarConfig = async () => {
    try {
      const res = await financeFetch(`/api/finance/config?${qs()}`);
      if (res.ok) {
        const data = await res.json();
        setConfig({
          moedaBase: data.data?.moedaBase || 'BRL',
          moedas: data.data?.moedas || ['BRL'],
          cotacoes: data.data?.cotacoes || {},
          emitente: data.data?.emitente || {},
          dadosBancarios: data.data?.dadosBancarios || {},
          saldoFonteAcumulado: data.data?.saldoFonteAcumulado !== false,
        });
      }
    } catch (e) { console.error('Erro config financeira:', e); }
  };

  const carregarFontes = async () => {
    try {
      const [resFontes, resSaldos] = await Promise.all([
        financeFetch(`/api/finance/fontes?${qs()}`),
        financeFetch(`/api/finance/fontes/saldos?${qs()}`),
      ]);
      if (resFontes.ok) { const d = await resFontes.json(); setFontes(d.data || []); }
      if (resSaldos.ok) { const d = await resSaldos.json(); setSaldosFontes(d.data || []); }
    } catch (e) { console.error('Erro fontes:', e); }
  };

  const carregarPatrocinios = async () => {
    try {
      const res = await financeFetch(`/api/finance/patrocinios?${qs()}`);
      if (res.ok) { const d = await res.json(); setPatrocinios(d.data || []); }
    } catch (e) { console.error('Erro patrocinios:', e); }
  };

  const carregarCobrancas = async () => {
    try {
      const res = await financeFetch(`/api/finance/cobrancas?${qs()}`);
      if (res.ok) { const d = await res.json(); setCobrancas(d.data || []); }
    } catch (e) { console.error('Erro cobranças:', e); }
  };

  const baixarPdfCobranca = async (c: any) => {
    const res = await financeFetch(`/api/finance/cobrancas/${c._id}/pdf?${qs()}`);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      alert(err.error || 'Não foi possível gerar o PDF.');
      return;
    }
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(c.tipo === 'invoice' ? 'INVOICE' : 'ND')}-${c.numero}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const excluirCobranca = async (c: any) => {
    if (!window.confirm(`Excluir a cobrança nº ${c.numero}?`)) return;
    const res = await financeFetch(`/api/finance/cobrancas/${c._id}?${qs()}`, { method: 'DELETE' });
    if (res.ok) carregarCobrancas();
  };

  const carregarCubo = async () => {
    setCuboCarregando(true);
    try {
      const p = new URLSearchParams({
        empresaId, linhas: cuboLinhas, colunas: cuboColunas, ano: String(anoPainel),
      });
      if (cuboTipo) p.set('tipo', cuboTipo);
      if (cuboStatus) p.set('status', cuboStatus);
      if (moedaPainel) p.set('moeda', moedaPainel);
      const res = await financeFetch(`/api/finance/cubo?${p.toString()}`);
      if (res.ok) { const d = await res.json(); setCuboData(d.data || null); }
    } catch (e) {
      console.error('Erro relatório cubo:', e);
    } finally {
      setCuboCarregando(false);
    }
  };

  /** Baixa o cubo como CSV (abre direto no Excel). */
  const baixarCuboCsv = () => {
    if (!cuboData) return;
    const cols = cuboData.colunas.map((c: any) => c.chave);
    const sep = ';';
    const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const linhas = [
      [cuboData.dimensoes.rotuloLinhas, ...cols, 'Total'].map(esc).join(sep),
      ...cuboData.linhas.map((l: any) => [
        esc(l.chave),
        ...cols.map((c: string) => String(l.celulas[c] ?? 0).replace('.', ',')),
        String(l.total).replace('.', ','),
      ].join(sep)),
      ['Total', ...cuboData.colunas.map((c: any) => String(c.total).replace('.', ',')),
        String(cuboData.total).replace('.', ',')].map((x, i) => i === 0 ? esc(x) : x).join(sep),
    ].join('\n');
    const url = URL.createObjectURL(new Blob(['\ufeff' + linhas], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `cubo-${cuboLinhas}-por-${cuboColunas}-${anoPainel}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const carregarClientesCobranca = async () => {
    try {
      const res = await financeFetch(`/api/finance/cobrancas/clientes?${qs()}`);
      if (res.ok) { const d = await res.json(); setClientesCobranca(d.data || []); }
    } catch (e) { console.error('Erro clientes de cobrança:', e); }
  };

  /**
   * Emite a cobrança e já baixa o PDF. Os dados de quem cobra ficam salvos
   * na configuração da empresa — na próxima vez o formulário vem preenchido.
   */
  const emitirCobranca = async (p: PayloadCobranca) => {
    const res = await financeFetch(`/api/finance/cobrancas?${qs()}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...p, criadoPor: session.usuario.email }),
    });
    const d = await res.json().catch(() => ({}));
    // Erro 500 vem em HTML, não em JSON: sem o status na mensagem, a tela dizia
    // só "falha" e não dava para saber se foi permissão, dado inválido ou bug.
    if (!res.ok || !d.success) {
      throw new Error(d.error || `Falha ao emitir a cobrança (HTTP ${res.status}).`);
    }

    await financeFetch(`/api/finance/config?${qs()}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ emitente: p.emitente, dadosBancarios: p.dadosBancarios }),
    }).catch(() => null);

    const pdf = await financeFetch(`/api/finance/cobrancas/${d.data._id}/pdf?${qs()}`);
    if (pdf.ok) {
      const blob = await pdf.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(p.tipo === 'invoice' ? 'INVOICE' : 'ND')}-${d.data.numero}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } else {
      const err = await pdf.json().catch(() => ({}));
      alert(err.error || `A cobrança nº ${d.data.numero} foi salva, mas o PDF falhou (HTTP ${pdf.status}). Baixe de novo em Cobranças.`);
    }
    setCobrando(null);
    carregarConfig();
    carregarClientesCobranca();
  };

  const carregarResumoAno = async () => {
    try {
      const res = await financeFetch(
        `/api/finance/resumo-ano?${qs()}&ano=${anoPainel}${moedaPainel ? `&moeda=${moedaPainel}` : ''}`);
      if (res.ok) { const d = await res.json(); setResumoAno(d.data || null); }
    } catch (e) { console.error('Erro resumo do ano:', e); }
  };

  const carregarReembolsos = async () => {
    try {
      const res = await financeFetch(`/api/finance/reembolsos?${qs()}&status=${filtroReembolso}`);
      if (res.ok) {
        const d = await res.json();
        setReembolsos(d.data || []);
        setReembolsosResumo(d.resumo || { totalBase: 0, quantidade: 0, porFonte: [] });
      }
    } catch (e) { console.error('Erro reembolsos:', e); }
  };

  /** Tudo que muda de número quando um lançamento muda. */
  const recarregarTudo = () => {
    carregarContas();
    carregarDashboard();
    carregarFontes();
    carregarPatrocinios();
    carregarResumoAno();
    carregarReembolsos();
  };

  const salvarFonte = async (dados: Record<string, unknown>) => {
    const editando = fonteEditando?._id;
    try {
      const res = await fetch(
        editando ? `/api/finance/fontes/${encodeURIComponent(editando)}` : '/api/finance/fontes',
        {
          method: editando ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...dados, empresaId, criadoPor: session.usuario.email }),
        }
      );
      if (res.ok) { carregarFontes(); carregarResumoAno(); setShowModal(null); setFonteEditando(null); }
      else alert('Não consegui salvar a fonte.');
    } catch (e) { console.error('Erro ao salvar fonte:', e); }
  };

  const excluirFonte = async (fonte: Fonte | SaldoFonte) => {
    if (!window.confirm(`Excluir a fonte "${fonte.nome}"?`)) return;
    try {
      const res = await financeFetch(`/api/finance/fontes/${encodeURIComponent(fonte._id)}`, { method: 'DELETE' });
      if (res.ok) { carregarFontes(); carregarResumoAno(); return; }
      // 409 = fonte em uso; o backend explica o porquê
      const erro = await res.json().catch(() => ({}));
      alert(erro.error || 'Não consegui excluir a fonte.');
    } catch (e) { console.error('Erro ao excluir fonte:', e); }
  };

  const salvarPatrocinio = async (dados: Record<string, unknown>) => {
    const editando = patrocinioEditando?._id;
    try {
      const res = await fetch(
        editando ? `/api/finance/patrocinios/${encodeURIComponent(editando)}` : '/api/finance/patrocinios',
        {
          method: editando ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...dados, empresaId, criadoPor: session.usuario.email }),
        }
      );
      if (res.ok) { recarregarTudo(); setShowModal(null); setPatrocinioEditando(null); }
      else alert('Não consegui salvar o patrocínio.');
    } catch (e) { console.error('Erro ao salvar patrocinio:', e); }
  };

  const excluirPatrocinio = async (pat: Patrocinio) => {
    if (!window.confirm(
      `Excluir o patrocínio "${pat.patrocinador}"?\n\n` +
      'As contas a receber já lançadas continuam no financeiro, só perdem o vínculo com o contrato.'
    )) return;
    try {
      const res = await financeFetch(`/api/finance/patrocinios/${encodeURIComponent(pat._id)}`, { method: 'DELETE' });
      if (res.ok) recarregarTudo();
    } catch (e) { console.error('Erro ao excluir patrocinio:', e); }
  };

  const gerarRecebiveis = async (pat: Patrocinio) => {
    try {
      const res = await financeFetch(`/api/finance/patrocinios/${encodeURIComponent(pat._id)}/gerar-recebiveis`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ criadoPor: session.usuario.email }),
      });
      if (res.ok) {
        const d = await res.json();
        recarregarTudo();
        alert(`${d.geradas} parcela(s) viraram contas a receber.`);
      }
    } catch (e) { console.error('Erro ao gerar recebiveis:', e); }
  };

  const marcarContrapartida = async (pat: Patrocinio, c: Contrapartida, concluidas: number) => {
    try {
      const res = await fetch(
        `/api/finance/patrocinios/${encodeURIComponent(pat._id)}/contrapartidas/${encodeURIComponent(c.id || '')}`,
        { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ concluidas }) }
      );
      if (res.ok) { carregarPatrocinios(); carregarResumoAno(); }
    } catch (e) { console.error('Erro ao marcar contrapartida:', e); }
  };

  /** Salva a config. Mexer no saldo das fontes muda o painel, então recarrega. */
  const salvarConfig = async (dados: Partial<FinanceConfig>) => {
    try {
      const res = await financeFetch('/api/finance/config', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...dados, empresaId }),
      });
      if (res.ok) {
        await carregarConfig();
        // mudar a moeda base muda todo total consolidado da tela
        recarregarTudo();
      } else alert('Não consegui salvar as moedas.');
    } catch (e) { console.error('Erro ao salvar config:', e); }
  };

  const quitarReembolso = async (item: { _id: string }, desfazer: boolean) => {
    try {
      const res = await financeFetch(`/api/finance/reembolsos/${encodeURIComponent(item._id)}/quitar`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ desfazer }),
      });
      if (res.ok) { carregarReembolsos(); carregarResumoAno(); }
    } catch (e) { console.error('Erro ao quitar reembolso:', e); }
  };

  const criarConta = async (tipo: 'pagar' | 'receber', dados: Record<string, unknown>) => {
    try {
      const endpoint = tipo === 'pagar' ? '/api/finance/contas-pagar' : '/api/finance/contas-receber';
      const res = await fetch(endpoint, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...dados, empresaId, criadoPor: session.usuario.email })
      });
      if (res.ok) { recarregarTudo(); setShowNovaConta(null); }
      else {
        const erro = await res.json().catch(() => null);
        alert(erro?.error || 'Não consegui salvar o lançamento.');
      }
    } catch (e) { console.error('Erro ao criar conta:', e); }
  };

  const rotaConta = (tipo: 'pagar' | 'receber') => tipo === 'pagar' ? 'contas-pagar' : 'contas-receber';

  const atualizarStatus = async (
    conta: ContaFinanceira, novoStatus: string, extra: Record<string, unknown> = {}
  ) => {
    try {
      const res = await financeFetch(`/api/finance/${rotaConta(conta.tipo)}/${encodeURIComponent(conta._id)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: novoStatus, ...extra })
      });
      if (res.ok) recarregarTudo();
      else alert('Não consegui dar baixa nesse título.');
    } catch (e) { console.error('Erro ao atualizar status:', e); }
  };

  const deletarConta = async (conta: ContaFinanceira) => {
    if (!window.confirm('Tem certeza que deseja excluir esta conta?')) return;
    try {
      const res = await financeFetch(`/api/finance/${rotaConta(conta.tipo)}/${encodeURIComponent(conta._id)}`, { method: 'DELETE' });
      if (res.ok) recarregarTudo();
    } catch (e) { console.error('Erro ao deletar conta:', e); }
  };

  const apiGenericCreate = async (endpoint: string, dados: Record<string, unknown>, onSuccess: () => void) => {
    try {
      const res = await financeFetch(`/api/finance/${endpoint}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...dados, empresaId, criadoPor: session.usuario.email })
      });
      if (res.ok) { onSuccess(); setShowModal(null); }
    } catch (e) { console.error('Erro ao criar:', e); }
  };

  const apiGenericDelete = async (endpoint: string, id: string, onSuccess: () => void) => {
    if (!window.confirm('Tem certeza que deseja excluir?')) return;
    try {
      const res = await financeFetch(`/api/finance/${endpoint}/${encodeURIComponent(id)}`, { method: 'DELETE' });
      if (res.ok) onSuccess();
    } catch (e) { console.error('Erro ao deletar:', e); }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert('Imagem deve ter no máximo 2MB'); return; }
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      setLogoUrl(result);
      localStorage.setItem(`erp_logo_${empresaId}`, result);
    };
    reader.readAsDataURL(file);
  };

  // Totais consolidados saem sempre na moeda base da empresa;
  // para o valor original de um lançamento use fmt(valor, conta.moeda).
  const formatarMoeda = (valor: number, moeda?: string) => fmt(valor, moeda || config.moedaBase);
  // Moeda em que o painel está sendo exibido (o backend confirma qual usou).
  const moedaPainelEfetiva = dashboardData?.moeda || moedaPainel || config.moedaBase;
  const fmtPainel = (valor: number) => fmt(valor, moedaPainelEfetiva);
  // Nunca usar new Date('YYYY-MM-DD'): isso é lido como UTC e, em fuso
  // negativo, exibe o dia anterior (era o 15/04 no lugar do 16/04).
  const formatarData = (data: string) => fmtData(data);

  // Linhas da grade do GIX após o filtro por nome da despesa
  const orcItensFiltrados = (orcGix?.itens || []).filter(i =>
    !orcBusca.trim() || i.despesa.toLowerCase().includes(orcBusca.trim().toLowerCase()));

  // 'YYYY-MM' -> 'JAN/2026' (mesmo rótulo que o GIX usa na grade de orçamento)
  const rotuloCompetencia = (comp: string) => {
    const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
    const [ano, mes] = comp.split('-');
    return `${MESES[Number(mes) - 1] || mes}/${ano}`;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pago': case 'recebido': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'pendente': return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'vencido': return <AlertTriangle className="w-4 h-4 text-red-500" />;
      default: return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pago': case 'recebido': return 'bg-green-100 text-green-700';
      case 'pendente': return 'bg-yellow-100 text-yellow-700';
      case 'vencido': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getCompraStatusColor = (status: string) => {
    switch (status) {
      case 'rascunho': return 'bg-gray-100 text-gray-700';
      case 'aprovado': return 'bg-blue-100 text-blue-700';
      case 'pedido': return 'bg-yellow-100 text-yellow-700';
      case 'recebido': return 'bg-green-100 text-green-700';
      case 'cancelado': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const filtrarContas = (contas: ContaFinanceira[]) => {
    return contas.filter(c => {
      if (filtroStatus !== 'todos' && c.status !== filtroStatus) return false;
      if (busca && !c.descricao.toLowerCase().includes(busca.toLowerCase()) &&
          !(c.fornecedor || '').toLowerCase().includes(busca.toLowerCase()) &&
          !(c.cliente || '').toLowerCase().includes(busca.toLowerCase())) return false;
      return true;
    });
  };

  // `mod`/`feat` casam com os nomes usados em Configurações, que é o que
  // liga e desliga cada item do menu.
  const todasMenuSections = [
    { label: 'FINANCEIRO', mod: 'Financeiro', items: [
      { id: 'dashboard' as Section, icon: BarChart3, label: 'Dashboard', feat: 'Dashboard' },
      { id: 'contas-pagar' as Section, icon: TrendingDown, label: 'Contas a Pagar', feat: 'Contas a Pagar' },
      { id: 'contas-receber' as Section, icon: TrendingUp, label: 'Contas a Receber', feat: 'Contas a Receber' },
      { id: 'cobrancas' as Section, icon: FileDown, label: 'Cobranças', feat: 'Cobranças' },
      { id: 'relatorios' as Section, icon: Layers, label: 'Relatórios', feat: 'Relatórios' },
      { id: 'orcamentos' as Section, icon: Calculator, label: 'Orçamento', feat: 'Orçamento' },
      { id: 'caixas-bancos' as Section, icon: Landmark, label: 'Caixas e Bancos', feat: 'Caixas e Bancos' },
    ]},
    { label: 'CONTRATOS', mod: 'Contratos', items: [
      { id: 'patrocinios' as Section, icon: Handshake, label: 'Contratos', feat: 'Contratos' },
      { id: 'fontes' as Section, icon: Wallet, label: 'Fontes de Recurso', feat: 'Fontes de Recurso' },
      { id: 'reembolsos' as Section, icon: Undo2, label: 'Reembolsos', feat: 'Reembolsos' },
    ]},
    { label: 'COMPRAS', mod: 'Compras', items: [
      { id: 'compras' as Section, icon: ShoppingCart, label: 'Ordens de Compra', feat: 'Ordens de Compra' },
      { id: 'fornecedores' as Section, icon: Truck, label: 'Fornecedores', feat: 'Fornecedores' },
      { id: 'compradores' as Section, icon: UserCheck, label: 'Compradores', feat: 'Compradores' },
    ]},
    { label: 'ESTOQUE', mod: 'Estoque', items: [
      { id: 'produtos' as Section, icon: PackageSearch, label: 'Produtos', feat: 'Produtos' },
      { id: 'estoque' as Section, icon: Boxes, label: 'Estoque', feat: 'Controle de Estoque' },
      { id: 'inventario' as Section, icon: ClipboardList, label: 'Inventário', feat: 'Inventário' },
    ]},
    { label: 'CADASTROS', mod: 'Cadastros', items: [
      { id: 'categorias' as Section, icon: Tag, label: 'Categorias', feat: 'Categorias' },
      { id: 'centros-custo' as Section, icon: Building2, label: 'Centro de Custos', feat: 'Centro de Custos' },
      { id: 'plano-contas' as Section, icon: FileText, label: 'Plano de Contas', feat: 'Plano de Contas' },
    ]},
  ];

  // Só entra no menu o que estiver ligado em Configurações.
  const featAtiva = (mod: string, feat: string) => modulesConfig[mod]?.[feat] !== false;
  const menuSections = todasMenuSections
    .map(s => ({ ...s, items: s.items.filter(i => featAtiva(s.mod, i.feat)) }))
    .filter(s => s.items.length > 0);

  const secoesVisiveis = new Set<string>([
    ...menuSections.flatMap(s => s.items.map(i => i.id as string)),
    'configuracoes',
  ]);

  // Desligar o módulo da tela aberta jogaria o usuário numa página em branco.
  useEffect(() => {
    if (!secoesVisiveis.has(currentSection)) {
      const primeira = menuSections[0]?.items[0]?.id;
      if (primeira) setCurrentSection(primeira);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modulesConfig, currentSection]);

  const sectionTitles: Record<Section, string> = {
    'dashboard': 'Dashboard Financeiro',
    'contas-pagar': 'Contas a Pagar',
    'contas-receber': 'Contas a Receber',
    'cobrancas': 'Cobranças (Invoice / Nota de Débito)',
    'relatorios': 'Relatórios',
    'orcamentos': 'Orçamento',
    'caixas-bancos': 'Caixas e Bancos',
    'patrocinios': 'Contratos',
    'fontes': 'Fontes de Recurso',
    'reembolsos': 'Reembolsos',
    'categorias': 'Categorias',
    'centros-custo': 'Centro de Custos',
    'plano-contas': 'Plano de Contas',
    'compras': 'Ordens de Compra',
    'fornecedores': 'Fornecedores',
    'compradores': 'Compradores',
    'produtos': 'Produtos',
    'estoque': 'Estoque',
    'inventario': 'Inventário',
    'configuracoes': 'Configurações',
  };

  const sectionIcons: Record<Section, React.ElementType> = {
    'dashboard': LayoutDashboard,
    'contas-pagar': TrendingDown,
    'contas-receber': TrendingUp,
    'cobrancas': FileDown,
    'relatorios': Layers,
    'orcamentos': Calculator,
    'caixas-bancos': Landmark,
    'patrocinios': Handshake,
    'fontes': Wallet,
    'reembolsos': Undo2,
    'categorias': Tag,
    'centros-custo': Building2,
    'plano-contas': FileText,
    'compras': ShoppingCart,
    'fornecedores': Truck,
    'compradores': UserCheck,
    'produtos': Package,
    'estoque': Boxes,
    'inventario': ClipboardList,
    'configuracoes': Settings,
  };

  const showNewButton = ['contas-pagar', 'contas-receber', 'cobrancas', 'orcamentos', 'caixas-bancos', 'patrocinios', 'fontes', 'categorias', 'centros-custo', 'plano-contas', 'compras', 'fornecedores', 'compradores', 'produtos', 'estoque', 'inventario'].includes(currentSection);
  const newButtonLabels: Record<string, string> = {
    'contas-pagar': 'Nova Conta', 'contas-receber': 'Nova Conta',
    'cobrancas': 'Nova Cobrança',
    'orcamentos': 'Nova Linha Orçamentária',
    'caixas-bancos': 'Nova Conta Bancária',
    'patrocinios': 'Novo Contrato', 'fontes': 'Nova Fonte',
    'categorias': 'Nova Categoria', 'centros-custo': 'Novo Centro',
    'plano-contas': 'Nova Conta', 'compras': 'Nova Compra',
    'fornecedores': 'Novo Fornecedor', 'compradores': 'Novo Comprador',
    'produtos': 'Novo Produto', 'estoque': 'Novo Item', 'inventario': 'Novo Inventário',
  };

  const handleNewButton = () => {
    if (currentSection === 'cobrancas') setCobrando({} as ContaFinanceira);   // cobrança avulsa
    else if (currentSection === 'contas-pagar') setShowNovaConta('pagar');
    else if (currentSection === 'contas-receber') setShowNovaConta('receber');
    else if (currentSection === 'fontes') { setFonteEditando(null); setShowModal('fontes'); }
    else if (currentSection === 'patrocinios') { setPatrocinioEditando(null); setShowModal('patrocinios'); }
    else setShowModal(currentSection);
  };

  return (
    <div className="h-screen flex bg-gray-50">
      {/* Sidebar Azul */}
      <div className={`${menuOpen ? 'w-60' : 'w-16'} bg-gradient-to-b from-[#0a1628] to-[#142240] flex flex-col transition-all duration-300`}>
        {/* Toggle + Logo da Empresa */}
        <div className="p-4 border-b border-white/10">
          <div className={`flex ${menuOpen ? 'justify-end' : 'justify-center'} mb-2`}>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="p-1.5 rounded-lg text-blue-300/60 hover:bg-white/10 hover:text-white transition-all"
              title={menuOpen ? 'Recolher menu' : 'Expandir menu'}
            >
              {menuOpen ? <ChevronsLeft className="w-4 h-4" /> : <ChevronsRight className="w-4 h-4" />}
            </button>
          </div>
          <div className={`flex ${menuOpen ? 'flex-col items-center' : 'items-center justify-center'} gap-3`}>
            {logoUrl ? (
              <label className="relative group cursor-pointer" title="Clique para trocar a logo">
                <img src={logoUrl} alt="Logo" className={`${menuOpen ? 'w-24 h-24' : 'w-10 h-10'} rounded-2xl object-cover border-2 border-white/20 shadow-lg transition-all duration-300`} />
                <div className={`absolute inset-0 rounded-2xl bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200`}>
                  <Edit2 className={`${menuOpen ? 'w-6 h-6' : 'w-4 h-4'} text-white`} />
                </div>
                <input type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
              </label>
            ) : (
              <label className={`${menuOpen ? 'w-24 h-24' : 'w-10 h-10'} rounded-2xl bg-white/10 border-2 border-dashed border-white/30 flex items-center justify-center cursor-pointer hover:bg-white/20 transition-all duration-300`} title="Clique para adicionar logo">
                <Image className={`${menuOpen ? 'w-10 h-10' : 'w-5 h-5'} text-white/50`} />
                <input type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
              </label>
            )}
            {menuOpen && (
              <div className="text-center w-full">
                <h1 className="font-bold text-white text-sm leading-tight truncate">{session.empresa?.nome || 'Finance ERP'}</h1>
              </div>
            )}
          </div>
        </div>

        {/* Menu Sections */}
        <nav className="flex-1 pt-1 pb-2 px-2 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          {menuSections.map(section => {
            const isExpanded = expandedSections[section.label] !== false;
            const hasActiveItem = section.items.some(item => item.id === currentSection);
            return (
              <div key={section.label} className="mb-1">
                {menuOpen ? (
                  <button
                    onClick={() => toggleSection(section.label)}
                    className="w-full flex items-center justify-between px-3 py-1.5 group"
                  >
                    <span className={`text-[10px] font-bold uppercase tracking-widest transition-colors ${hasActiveItem ? 'text-blue-400' : 'text-blue-400/60 group-hover:text-blue-400/80'}`}>{section.label}</span>
                    {isExpanded ? (
                      <ChevronDown className="w-3 h-3 text-blue-400/40 group-hover:text-blue-400/70 transition-colors" />
                    ) : (
                      <ChevronRight className="w-3 h-3 text-blue-400/40 group-hover:text-blue-400/70 transition-colors" />
                    )}
                  </button>
                ) : null}
                <div className={`space-y-0.5 overflow-hidden transition-all duration-200 ${!menuOpen || isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                  {section.items.map(item => (
                    <button
                      key={item.id}
                      onClick={() => { setCurrentSection(item.id); setBusca(''); setFiltroStatus('todos'); }}
                      title={!menuOpen ? item.label : undefined}
                      className={`w-full flex items-center ${menuOpen ? 'gap-3' : 'justify-center'} px-3 py-2 rounded-xl text-sm font-medium transition-all
                        ${currentSection === item.id 
                          ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30' 
                          : 'text-blue-200/70 hover:bg-white/5 hover:text-white'}`}
                    >
                      <item.icon className={`w-4 h-4 ${currentSection === item.id ? 'text-white' : 'text-blue-400/60'}`} />
                      {menuOpen && <span>{item.label}</span>}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-3 border-t border-white/10 space-y-1">
          <button
            onClick={() => { setCurrentSection('configuracoes'); setBusca(''); setFiltroStatus('todos'); }}
            title={!menuOpen ? 'Configurações' : undefined}
            className={`w-full flex items-center ${menuOpen ? 'gap-2' : 'justify-center'} px-3 py-2 rounded-xl text-sm transition-all
              ${currentSection === 'configuracoes' 
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30 font-medium' 
                : 'text-blue-300/60 hover:bg-white/5 hover:text-white'}`}
          >
            <Settings className={`w-4 h-4 ${currentSection === 'configuracoes' ? 'text-white' : 'text-blue-400/60'}`} />
            {menuOpen && <span>Configurações</span>}
          </button>
          <button
            onClick={onVoltar}
            title={!menuOpen ? 'Voltar ao Escalone' : undefined}
            className={`w-full flex items-center ${menuOpen ? 'gap-2' : 'justify-center'} px-3 py-2 rounded-xl text-sm text-blue-300/60 hover:bg-white/5 hover:text-white transition-all`}
          >
            <ArrowLeft className="w-4 h-4" />
            {menuOpen && <span>Voltar ao Escalone</span>}
          </button>
        </div>
      </div>

      {/* Conteúdo Principal */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2.5">
                {(() => { const Icon = sectionIcons[currentSection]; return Icon ? <Icon className="w-5 h-5 text-blue-600" /> : null; })()}
                <h2 className="text-xl font-bold text-gray-900">{sectionTitles[currentSection]}</h2>
              </div>
              <p className="text-sm text-gray-500 mt-0.5">{session.empresa?.nome || 'Empresa'}</p>
            </div>
            <div className="flex items-center gap-3">
              {showNewButton && (
                <button
                  onClick={handleNewButton}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl text-sm font-semibold hover:shadow-lg hover:shadow-blue-600/25 transition-all"
                >
                  <Plus className="w-4 h-4" />
                  {newButtonLabels[currentSection]}
                </button>
              )}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-lg border border-blue-100">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-white text-xs font-bold">
                  {session.usuario.nome?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <span className="text-sm font-medium text-gray-700">{session.usuario.nome?.split(' ')[0]}</span>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">

          {/* ═══ DASHBOARD ═══ */}
          {currentSection === 'dashboard' && (
            <div className="space-y-6">
              {/* Ano e moeda do painel: o ano era fixo no corrente e a moeda
                  era sempre a base da empresa. Trocar a moeda converte tudo
                  pela cotação de hoje. */}
              <div className="flex flex-wrap items-center gap-3 bg-white rounded-2xl border border-gray-200 px-4 py-3 shadow-sm">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-500">Ano</span>
                  <select value={anoPainel} onChange={e => setAnoPainel(Number(e.target.value))}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500">
                    {(resumoAno?.anosComLancamento?.length
                      ? resumoAno.anosComLancamento
                      : [new Date().getFullYear()]
                    ).map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-500">Exibir em</span>
                  <select value={moedaPainel} onChange={e => setMoedaPainel(e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-blue-500">
                    <option value="">{config.moedaBase} (moeda base)</option>
                    {(config.moedas || []).filter(m => m !== config.moedaBase)
                      .map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                {!!resumoAno?.cambio && (
                  <span className="text-xs text-gray-400">
                    {resumoAno.cambio.aviso
                      ? `⚠️ ${resumoAno.cambio.aviso}`
                      : `Convertido pela cotação de ${resumoAno.cambio.atualizadoEm
                          ? new Date(resumoAno.cambio.atualizadoEm).toLocaleString('pt-BR')
                          : 'hoje'}${resumoAno.cambio.fonte ? ` · ${resumoAno.cambio.fonte}` : ''}`}
                  </span>
                )}
              </div>

              {/* Contratos, reembolsos, moedas e fontes */}
              <PainelAnual
                resumo={resumoAno}
                onIrPara={(secao) => setCurrentSection(secao as Section)}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                      <TrendingDown className="w-5 h-5 text-red-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-500">A Pagar</span>
                  </div>
                  <p className="text-2xl font-bold text-red-600">{fmtPainel(dashboardData?.pagar.pendente || 0)}</p>
                  <p className="text-xs text-gray-400 mt-1">Pendente</p>
                </div>
                <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-500">A Receber</span>
                  </div>
                  <p className="text-2xl font-bold text-green-600">{fmtPainel(dashboardData?.receber.pendente || 0)}</p>
                  <p className="text-xs text-gray-400 mt-1">Pendente</p>
                </div>
                <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Wallet className="w-5 h-5 text-blue-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-500">Saldo Atual</span>
                  </div>
                  <p className={`text-2xl font-bold ${(dashboardData?.saldo || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                    {fmtPainel(dashboardData?.saldo || 0)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Realizado</p>
                </div>
                <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                      <PiggyBank className="w-5 h-5 text-indigo-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-500">Saldo Previsto</span>
                  </div>
                  <p className={`text-2xl font-bold ${(dashboardData?.saldo_previsto || 0) >= 0 ? 'text-indigo-600' : 'text-red-600'}`}>
                    {fmtPainel(dashboardData?.saldo_previsto || 0)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Incluindo pendentes</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <TrendingDown className="w-4 h-4 text-red-500" /> Contas a Pagar Recentes
                    </h3>
                    <button onClick={() => setCurrentSection('contas-pagar')} className="text-xs text-blue-600 hover:underline font-medium">Ver todas →</button>
                  </div>
                  {contasPagar.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <Receipt className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Nenhuma conta a pagar</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {contasPagar.slice(0, 5).map((conta) => (
                        <div key={conta._id} className="flex items-center justify-between p-2.5 rounded-lg hover:bg-gray-50 border border-gray-100">
                          <div className="flex items-center gap-3">
                            {getStatusIcon(conta.status)}
                            <div>
                              <p className="text-sm font-medium text-gray-900">{conta.descricao}</p>
                              <p className="text-xs text-gray-400">{conta.fornecedor} • {formatarData(conta.vencimento)}</p>
                            </div>
                          </div>
                          <span className="text-sm font-semibold text-red-600">{formatarMoeda(conta.valor)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-green-500" /> Contas a Receber Recentes
                    </h3>
                    <button onClick={() => setCurrentSection('contas-receber')} className="text-xs text-blue-600 hover:underline font-medium">Ver todas →</button>
                  </div>
                  {contasReceber.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <Receipt className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Nenhuma conta a receber</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {contasReceber.slice(0, 5).map((conta) => (
                        <div key={conta._id} className="flex items-center justify-between p-2.5 rounded-lg hover:bg-gray-50 border border-gray-100">
                          <div className="flex items-center gap-3">
                            {getStatusIcon(conta.status)}
                            <div>
                              <p className="text-sm font-medium text-gray-900">{conta.descricao}</p>
                              <p className="text-xs text-gray-400">{conta.cliente} • {formatarData(conta.vencimento)}</p>
                            </div>
                          </div>
                          <span className="text-sm font-semibold text-green-600">{formatarMoeda(conta.valor)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {dashboardData?.categorias && dashboardData.categorias.length > 0 && (
                <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-blue-500" /> Top Categorias por Valor
                  </h3>
                  <div className="space-y-3">
                    {dashboardData.categorias.map((cat, i) => {
                      const maxVal = dashboardData.categorias[0]?.total || 1;
                      const pct = (cat.total / maxVal) * 100;
                      return (
                        <div key={i} className="flex items-center gap-3">
                          <span className="text-sm text-gray-600 w-32 truncate">{cat._id || 'Sem categoria'}</span>
                          <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
                            <div className="h-full rounded-full bg-gradient-to-r from-blue-400 to-blue-600 transition-all" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="text-sm font-semibold text-gray-900 w-28 text-right">{fmtPainel(cat.total)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ═══ CONTAS A PAGAR / RECEBER ═══ */}
          {(currentSection === 'contas-pagar' || currentSection === 'contas-receber') && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type="text" value={busca} onChange={e => setBusca(e.target.value)}
                    placeholder="Buscar por descrição, fornecedor ou cliente..."
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
                </div>
                <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}
                  className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400">
                  <option value="todos">Todos</option>
                  <option value="pendente">Pendente</option>
                  <option value={currentSection === 'contas-pagar' ? 'pago' : 'recebido'}>
                    {currentSection === 'contas-pagar' ? 'Pago' : 'Recebido'}
                  </option>
                  <option value="vencido">Vencido</option>
                </select>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Descrição</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">{currentSection === 'contas-pagar' ? 'Fornecedor' : 'Cliente'}</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Categoria</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">
                        {currentSection === 'contas-pagar' ? 'Saiu de' : 'Entrou em'}
                      </th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Vencimento</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Valor</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtrarContas(currentSection === 'contas-pagar' ? contasPagar : contasReceber).length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center py-12 text-gray-400">
                          <Receipt className="w-10 h-10 mx-auto mb-3 opacity-40" />
                          <p className="text-sm font-medium">Nenhuma conta encontrada</p>
                          <p className="text-xs mt-1">Clique em "Nova Conta" para adicionar</p>
                        </td>
                      </tr>
                    ) : (
                      filtrarContas(currentSection === 'contas-pagar' ? contasPagar : contasReceber).map((conta) => (
                        <tr key={conta._id} onClick={() => setDetalhando(conta)}
                          className="hover:bg-gray-50 transition-colors cursor-pointer">
                          <td className="px-5 py-3">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(conta.status)}`}>
                              {getStatusIcon(conta.status)} {conta.status}
                            </span>
                          </td>
                          <td className="px-5 py-3">
                            <p className="text-sm font-medium text-gray-900">{conta.descricao}</p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              {conta.reembolsavel && !conta.reembolsado && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-100 rounded px-1.5 py-0.5">
                                  <Undo2 className="w-2.5 h-2.5" /> a devolver
                                </span>
                              )}
                              {!!conta.patrocinioId && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 rounded px-1.5 py-0.5">
                                  <Handshake className="w-2.5 h-2.5" /> patrocínio
                                </span>
                              )}
                              {!!conta.anexos?.length && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-gray-500 bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5">
                                  <Paperclip className="w-2.5 h-2.5" /> {conta.anexos.length}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-5 py-3 text-sm text-gray-600">{conta.fornecedor || conta.cliente || '-'}</td>
                          <td className="px-5 py-3 text-sm text-gray-600">{conta.categoria || '-'}</td>
                          <td className="px-5 py-3 text-sm text-gray-600">{conta.fonteNome || '-'}</td>
                          <td className="px-5 py-3 text-sm text-gray-600">{formatarData(conta.vencimento)}</td>
                          <td className={`px-5 py-3 text-sm font-bold text-right ${currentSection === 'contas-pagar' ? 'text-red-600' : 'text-green-600'}`}>
                            {fmt(conta.valor, conta.moeda || config.moedaBase)}
                            {(conta.moeda || config.moedaBase) !== config.moedaBase && (
                              <span className="block text-[11px] font-normal text-gray-400">
                                {fmt(conta.valorBase ?? conta.valor, config.moedaBase)}
                              </span>
                            )}
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center justify-end gap-1"
                              onClick={e => e.stopPropagation()}>
                              {conta.status === 'pendente' && (
                                <button onClick={() => setBaixando(conta)}
                                  className="p-1.5 text-green-500 hover:bg-green-50 rounded-lg transition-colors"
                                  title={currentSection === 'contas-pagar' ? 'Marcar como pago' : 'Marcar como recebido'}>
                                  <CheckCircle className="w-4 h-4" />
                                </button>
                              )}
                              {currentSection === 'contas-receber' && (
                                <button onClick={() => setCobrando(conta)}
                                  className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                                  title="Emitir invoice / nota de débito">
                                  <FileDown className="w-4 h-4" />
                                </button>
                              )}
                              <button onClick={() => deletarConta(conta)} className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors" title="Excluir">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}


          {/* ═══ COBRANÇAS (invoice / nota de débito) ═══ */}
          {currentSection === 'cobrancas' && (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <p className="text-sm font-semibold text-gray-900">Documentos emitidos</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  O PDF para mandar ao pagador. Dá para emitir daqui ou pelo botão
                  <FileDown className="w-3 h-3 inline mx-1" />
                  na linha de uma conta a receber.
                </p>
              </div>
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Nº</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Documento</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Cliente</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Emissão</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Vencimento</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Valor</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {!cobrancas.length ? (
                    <tr>
                      <td colSpan={7} className="text-center py-12 text-gray-400">
                        <FileDown className="w-10 h-10 mx-auto mb-3 opacity-40" />
                        <p className="text-sm font-medium">Nenhuma cobrança emitida</p>
                        <p className="text-xs mt-1">Clique em "Nova Cobrança" para gerar a primeira</p>
                      </td>
                    </tr>
                  ) : cobrancas.map(c => (
                    <tr key={c._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3 text-sm font-bold text-gray-900">{c.numero}</td>
                      <td className="px-5 py-3">
                        <span className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                          {c.tipo === 'invoice' ? 'Invoice' : 'Nota de Débito'}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-700">{c.cliente?.nome || '—'}</td>
                      <td className="px-5 py-3 text-sm text-gray-600">{fmtData(c.emitidaEm)}</td>
                      <td className="px-5 py-3 text-sm text-gray-600">{fmtData(c.vencimento)}</td>
                      <td className="px-5 py-3 text-sm font-bold text-gray-900 text-right">{fmt(c.total, c.moeda)}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => baixarPdfCobranca(c)}
                            className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg" title="Baixar PDF">
                            <FileDown className="w-4 h-4" />
                          </button>
                          <button onClick={() => excluirCobranca(c)}
                            className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg" title="Excluir">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ═══ RELATÓRIOS — cubo (tabela cruzada) ═══ */}
          {currentSection === 'relatorios' && (
            <div className="space-y-4">
              <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <label className="block text-[11px] font-medium text-gray-500 mb-1">Lançamentos</label>
                    <select value={cuboTipo} onChange={e => setCuboTipo(e.target.value)}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm">
                      <option value="pagar">A pagar</option>
                      <option value="receber">A receber</option>
                      <option value="">Tudo</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-gray-500 mb-1">Linhas</label>
                    <select value={cuboLinhas} onChange={e => setCuboLinhas(e.target.value)}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm">
                      {DIMENSOES_CUBO.map(d => <option key={d.valor} value={d.valor}>{d.rotulo}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-gray-500 mb-1">Colunas</label>
                    <select value={cuboColunas} onChange={e => setCuboColunas(e.target.value)}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm">
                      {DIMENSOES_CUBO.map(d => <option key={d.valor} value={d.valor}>{d.rotulo}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] font-medium text-gray-500 mb-1">Situação</label>
                    <select value={cuboStatus} onChange={e => setCuboStatus(e.target.value)}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm">
                      <option value="">Todas</option>
                      <option value="pendente">Pendente</option>
                      <option value="pago">Pago</option>
                      <option value="recebido">Recebido</option>
                      <option value="vencido">Vencido</option>
                    </select>
                  </div>
                  <div className="flex-1" />
                  <button onClick={baixarCuboCsv} disabled={!cuboData}
                    className="px-3 py-1.5 text-sm font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg flex items-center gap-1.5 disabled:opacity-50">
                    <FileDown className="w-4 h-4" /> CSV
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Ano {anoPainel} · valores em {cuboData?.moeda || config.moedaBase}
                  {cuboData?.cambio?.fonte ? ' (convertidos pela cotação de hoje)' : ''}
                  {' '}· o ano e a moeda vêm do seletor do Dashboard.
                </p>
              </div>

              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-x-auto">
                {cuboCarregando ? (
                  <p className="text-sm text-gray-400 text-center py-12">Calculando...</p>
                ) : !cuboData || !cuboData.linhas.length ? (
                  <div className="text-center py-12 text-gray-400">
                    <Layers className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    <p className="text-sm font-medium">Nada lançado nesse recorte</p>
                    <p className="text-xs mt-1">Tente outro ano, outra situação ou outro tipo</p>
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase sticky left-0 bg-gray-50">
                          {cuboData.dimensoes.rotuloLinhas}
                        </th>
                        {cuboData.colunas.map((c: any) => (
                          <th key={c.chave} className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">
                            {cuboData.dimensoes.colunas === 'mes' ? rotuloCompetencia(c.chave) : c.chave}
                          </th>
                        ))}
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {cuboData.linhas.map((l: any) => (
                        <tr key={l.chave} className="hover:bg-gray-50">
                          <td className="px-4 py-2.5 text-gray-800 font-medium sticky left-0 bg-white">
                            {l.chave || '—'}
                          </td>
                          {cuboData.colunas.map((c: any) => (
                            <td key={c.chave} className="px-4 py-2.5 text-right text-gray-600 whitespace-nowrap">
                              {l.celulas[c.chave] ? fmt(l.celulas[c.chave], cuboData.moeda) : '—'}
                            </td>
                          ))}
                          <td className="px-4 py-2.5 text-right font-bold text-gray-900 whitespace-nowrap">
                            {fmt(l.total, cuboData.moeda)}
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-gray-50 border-t-2 border-gray-200">
                        <td className="px-4 py-3 font-bold text-gray-900 sticky left-0 bg-gray-50">Total</td>
                        {cuboData.colunas.map((c: any) => (
                          <td key={c.chave} className="px-4 py-3 text-right font-bold text-gray-900 whitespace-nowrap">
                            {fmt(c.total, cuboData.moeda)}
                          </td>
                        ))}
                        <td className="px-4 py-3 text-right font-bold text-blue-700 whitespace-nowrap">
                          {fmt(cuboData.total, cuboData.moeda)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* ═══ PATROCÍNIOS ═══ */}
          {currentSection === 'patrocinios' && (
            <SecaoPatrocinios
              patrocinios={patrocinios}
              moedaBase={config.moedaBase}
              onEditar={p => { setPatrocinioEditando(p); setShowModal('patrocinios'); }}
              onExcluir={excluirPatrocinio}
              onGerarRecebiveis={gerarRecebiveis}
              onMarcarContrapartida={marcarContrapartida}
              userEmail={session.usuario.email}
            />
          )}

          {/* ═══ FONTES DE RECURSO ═══ */}
          {currentSection === 'fontes' && (
            <SecaoFontes
              saldos={saldosFontes}
              moedaBase={config.moedaBase}
              onEditar={f => { setFonteEditando(f); setShowModal('fontes'); }}
              onExcluir={excluirFonte}
            />
          )}

          {/* ═══ REEMBOLSOS ═══ */}
          {currentSection === 'reembolsos' && (
            <SecaoReembolsos
              itens={reembolsos}
              resumo={reembolsosResumo}
              moedaBase={config.moedaBase}
              filtro={filtroReembolso}
              onFiltro={setFiltroReembolso}
              onQuitar={quitarReembolso}
            />
          )}

          {/* ═══ CATEGORIAS ═══ */}
          {currentSection === 'categorias' && (
            <div className="space-y-4">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar categoria..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categorias.filter(c => !busca || c.nome.toLowerCase().includes(busca.toLowerCase())).length === 0 ? (
                  <div className="col-span-full text-center py-12 text-gray-400">
                    <Tag className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    <p className="text-sm font-medium">Nenhuma categoria cadastrada</p>
                    <p className="text-xs mt-1">Clique em "Nova Categoria" para adicionar</p>
                  </div>
                ) : (
                  categorias.filter(c => !busca || c.nome.toLowerCase().includes(busca.toLowerCase())).map(cat => (
                    <div key={cat._id} className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-all">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 rounded-full" style={{ backgroundColor: cat.cor || '#6366f1' }} />
                          <h4 className="font-semibold text-gray-900">{cat.nome}</h4>
                        </div>
                        <button onClick={() => apiGenericDelete('categorias', cat._id, carregarCategorias)}
                          className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-4 h-4" /></button>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                        cat.tipo === 'pagar' ? 'bg-red-100 text-red-700' : cat.tipo === 'receber' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {cat.tipo === 'pagar' ? 'Pagar' : cat.tipo === 'receber' ? 'Receber' : 'Ambos'}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* ═══ CENTRO DE CUSTOS ═══ */}
          {currentSection === 'centros-custo' && (
            <div className="space-y-4">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar centro de custo..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Código</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Nome</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Descrição</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Responsável</th>
                      <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {centrosCusto.filter(c => !busca || c.nome.toLowerCase().includes(busca.toLowerCase()) || c.codigo.toLowerCase().includes(busca.toLowerCase())).length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-12 text-gray-400">
                          <Building2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
                          <p className="text-sm font-medium">Nenhum centro de custo cadastrado</p>
                          <p className="text-xs mt-1">Clique em "Novo Centro" para adicionar</p>
                        </td>
                      </tr>
                    ) : (
                      centrosCusto.filter(c => !busca || c.nome.toLowerCase().includes(busca.toLowerCase()) || c.codigo.toLowerCase().includes(busca.toLowerCase())).map(cc => (
                        <tr key={cc._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-5 py-3 text-sm font-mono text-blue-600 font-semibold">{cc.codigo}</td>
                          <td className="px-5 py-3 text-sm font-medium text-gray-900">{cc.nome}</td>
                          <td className="px-5 py-3 text-sm text-gray-600">{cc.descricao || '-'}</td>
                          <td className="px-5 py-3 text-sm text-gray-600">{cc.responsavel || '-'}</td>
                          <td className="px-5 py-3 text-center">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${cc.ativo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              {cc.ativo ? 'Ativo' : 'Inativo'}
                            </span>
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => apiGenericDelete('centros-custo', cc._id, carregarCentrosCusto)}
                                className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors" title="Excluir"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ═══ PLANO DE CONTAS ═══ */}
          {currentSection === 'plano-contas' && (
            <div className="space-y-4">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar conta..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Código</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Nome</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Tipo</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Natureza</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Conta Pai</th>
                      <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {planoContas.filter(c => !busca || c.nome.toLowerCase().includes(busca.toLowerCase()) || c.codigo.toLowerCase().includes(busca.toLowerCase())).length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-12 text-gray-400">
                          <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
                          <p className="text-sm font-medium">Nenhuma conta cadastrada no plano</p>
                          <p className="text-xs mt-1">Clique em "Nova Conta" para adicionar</p>
                        </td>
                      </tr>
                    ) : (
                      planoContas.filter(c => !busca || c.nome.toLowerCase().includes(busca.toLowerCase()) || c.codigo.toLowerCase().includes(busca.toLowerCase())).map(pc => (
                        <tr key={pc._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-5 py-3 text-sm font-mono text-blue-600 font-semibold">{pc.codigo}</td>
                          <td className="px-5 py-3 text-sm font-medium text-gray-900">{pc.nome}</td>
                          <td className="px-5 py-3">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                              pc.tipo === 'sintetica' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                            }`}>{pc.tipo === 'sintetica' ? 'Sintética' : 'Analítica'}</span>
                          </td>
                          <td className="px-5 py-3">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                              pc.natureza === 'receita' ? 'bg-green-100 text-green-700' :
                              pc.natureza === 'despesa' ? 'bg-red-100 text-red-700' :
                              pc.natureza === 'ativo' ? 'bg-blue-100 text-blue-700' :
                              pc.natureza === 'passivo' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'
                            }`}>{pc.natureza.charAt(0).toUpperCase() + pc.natureza.slice(1)}</span>
                          </td>
                          <td className="px-5 py-3 text-sm text-gray-600">{pc.contaPai || '-'}</td>
                          <td className="px-5 py-3 text-center">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${pc.ativo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              {pc.ativo ? 'Ativo' : 'Inativo'}
                            </span>
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => apiGenericDelete('plano-contas', pc._id, carregarPlanoContas)}
                                className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors" title="Excluir"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ═══ ORÇAMENTO (BUDGET) ═══ */}
          {currentSection === 'orcamentos' && (
            <div className="space-y-4">

              {/* ═══ Fonte do orçamento: GIX (ERP) ou linhas manuais ═══ */}
              <div className="flex items-center gap-2">
                <button onClick={() => setOrcFonte('gix')}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors border ${
                    orcFonte === 'gix'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                  <Layers className="w-4 h-4 inline mr-1.5 -mt-0.5" />Orçamento do GIX
                </button>
                <button onClick={() => setOrcFonte('manual')}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors border ${
                    orcFonte === 'manual'
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                  <Calculator className="w-4 h-4 inline mr-1.5 -mt-0.5" />Linhas manuais
                </button>
              </div>

              {/* ═══════════════ ORÇAMENTO DO GIX (somente leitura) ═══════════════ */}
              {orcFonte === 'gix' && (
                <div className="space-y-4">
                  {/* Filtros */}
                  <div className="bg-white rounded-2xl border border-gray-200 p-4 flex flex-wrap items-end gap-3">
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">Escopo</label>
                      <div className="flex rounded-xl border border-gray-200 overflow-hidden">
                        {(['grupo', 'empresa', 'consolidado'] as const).map(t => (
                          <button key={t}
                            onClick={() => {
                              setOrcTipo(t);
                              if (t === 'grupo') setOrcCodigo(orcEscopos?.grupos?.[0]?.grupo ?? null);
                              else if (t === 'empresa') setOrcCodigo(orcEscopos?.empresas?.[0]?.empresa ?? null);
                              else setOrcCodigo(null);
                            }}
                            className={`px-3 py-2 text-sm font-medium transition-colors ${
                              orcTipo === t ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                            {t === 'grupo' ? 'Grupo' : t === 'empresa' ? 'Empresa' : 'Consolidado'}
                          </button>
                        ))}
                      </div>
                    </div>

                    {orcTipo !== 'consolidado' && (
                      <div>
                        <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">
                          {orcTipo === 'grupo' ? 'Grupo de empresa' : 'Empresa'}
                        </label>
                        <select value={orcCodigo ?? ''}
                          onChange={e => setOrcCodigo(e.target.value === '' ? null : Number(e.target.value))}
                          className="px-3 py-2 border border-gray-200 rounded-xl text-sm min-w-[220px] focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                          {orcTipo === 'grupo'
                            ? (orcEscopos?.grupos || []).map(g => (
                                <option key={g.grupo} value={g.grupo}>
                                  {String(g.grupo).padStart(3, '0')} - {g.nome || 'Grupo'} ({g.lancamentos} lanç.)
                                </option>))
                            : (orcEscopos?.empresas || []).map(e2 => (
                                <option key={e2.empresa} value={e2.empresa}>
                                  Empresa {String(e2.empresa).padStart(2, '0')} ({e2.lancamentos} lanç.)
                                </option>))}
                        </select>
                      </div>
                    )}

                    <div>
                      <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">Despesa</label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input type="text" value={orcBusca} onChange={e => setOrcBusca(e.target.value)}
                          placeholder="Filtrar despesa..."
                          className="pl-9 pr-3 py-2 border border-gray-200 rounded-xl text-sm w-52 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[11px] font-semibold text-gray-500 uppercase mb-1">Ano</label>
                      <select value={orcAno} onChange={e => setOrcAno(Number(e.target.value))}
                        className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                        {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() + 1 - i).map(a => (
                          <option key={a} value={a}>{a}</option>))}
                      </select>
                    </div>

                    <label className="flex items-center gap-2 text-sm text-gray-600 pb-2 cursor-pointer">
                      <input type="checkbox" checked={orcZerados} onChange={e => setOrcZerados(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300" />
                      Mostrar despesas zeradas
                    </label>

                    <div className="pb-2">
                      <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer"
                        title="Títulos a pagar já gerados, pela data de vencimento">
                        <input type="checkbox" checked={orcTitulos} onChange={e => setOrcTitulos(e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 accent-green-600" />
                        Títulos a pagar
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer mt-1.5"
                        title="Parcelas de ordens de compra em aberto, pela data de vencimento da parcela">
                        <input type="checkbox" checked={orcOcAberto} onChange={e => setOrcOcAberto(e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 accent-amber-600" />
                        OC em aberto
                      </label>
                      {orcOcAberto && (
                        <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer mt-1.5 ml-6"
                          title="O GIX soma só a OC liberada em 'Despesas + OC'; a não liberada aparece à parte">
                          <input type="checkbox" checked={orcOcSoLiberadas}
                            onChange={e => setOrcOcSoLiberadas(e.target.checked)}
                            className="w-3.5 h-3.5 rounded border-gray-300" />
                          contar só as liberadas
                        </label>
                      )}
                    </div>

                    <button onClick={carregarOrcamentoGix} disabled={orcLoading}
                      className="ml-auto px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 rounded-xl text-sm font-semibold text-gray-700 transition-colors">
                      <RefreshCw className={`w-4 h-4 inline mr-1.5 -mt-0.5 ${orcLoading ? 'animate-spin' : ''}`} />
                      Atualizar
                    </button>
                  </div>

                  {orcErro && (
                    <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-red-800">Não foi possível carregar o orçamento</p>
                        <p className="text-xs text-red-600 mt-0.5">{orcErro}</p>
                      </div>
                    </div>
                  )}

                  {/* Resumo */}
                  {orcGix && !orcErro && (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="bg-white rounded-2xl border border-gray-200 p-5">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Total Orçado</p>
                        <p className="text-2xl font-bold text-blue-700">{formatarMoeda(orcGix.total_geral)}</p>
                      </div>
                      <div className="bg-white rounded-2xl border border-gray-200 p-5">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Total Realizado</p>
                        <p className="text-2xl font-bold text-green-700">{formatarMoeda(orcGix.realizado_total_geral || 0)}</p>
                        {orcOcAberto && !!orcGix.oc_total_geral && (
                          <p className="text-[11px] text-amber-600 mt-1">
                            + {formatarMoeda(orcGix.oc_total_geral)} em OC liberada
                          </p>
                        )}
                        {orcOcAberto && !!orcGix.oc_nlib_total_geral && (
                          <p className="text-[11px] text-amber-400 mt-0.5">
                            ({formatarMoeda(orcGix.oc_nlib_total_geral)} em OC não liberada)
                          </p>
                        )}
                        {!!orcGix.realizado_fora_orcamento && (
                          <p className="text-[11px] text-gray-400 mt-0.5">
                            {formatarMoeda(orcGix.realizado_fora_orcamento)} pago fora do orçamento
                          </p>
                        )}
                      </div>
                      <div className="bg-white rounded-2xl border border-gray-200 p-5">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">% Consumido</p>
                        {(() => {
                          const consumido = (orcTitulos ? (orcGix.realizado_total_geral || 0) : 0)
                            + (orcOcAberto ? (orcGix.oc_total_geral || 0) : 0)
                            + (orcOcAberto && !orcOcSoLiberadas ? (orcGix.oc_nlib_total_geral || 0) : 0);
                          const pct = orcGix.total_geral ? Math.round(100 * consumido / orcGix.total_geral) : 0;
                          return (
                            <>
                              <p className={`text-2xl font-bold ${pct > 100 ? 'text-red-600' : pct > 80 ? 'text-yellow-600' : 'text-gray-800'}`}>{pct}%</p>
                              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mt-2">
                                <div className={`h-full rounded-full ${pct > 100 ? 'bg-red-500' : pct > 80 ? 'bg-yellow-500' : 'bg-green-500'}`}
                                  style={{ width: `${Math.min(pct, 100)}%` }} />
                              </div>
                            </>
                          );
                        })()}
                      </div>
                      <div className="bg-white rounded-2xl border border-gray-200 p-5">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Escopo</p>
                        <p className="text-lg font-bold text-gray-800 capitalize">
                          {orcGix.escopo.tipo}{orcGix.escopo.codigo != null ? ` ${String(orcGix.escopo.codigo).padStart(3, '0')}` : ''}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">{orcAno} · {orcGix.total_tipos_despesa} tipos</p>
                      </div>
                    </div>
                  )}


                  {/* Matriz tipo de despesa × mês */}
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[900px]">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase sticky left-0 bg-gray-50 z-10 min-w-[240px]">Despesa</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Realizado</th>
                            <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">% Cons.</th>
                            <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Total</th>
                            {(orcGix?.competencias || []).map(c => (
                              <th key={c} className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">
                                {rotuloCompetencia(c)}
                                <span className="block text-[9px] font-normal normal-case text-gray-400 leading-tight">
                                  orçado{orcTitulos && <> / <span className="text-green-600">títulos</span></>}
                                  {orcOcAberto && <> / <span className="text-amber-600">OC</span></>}
                                </span>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {orcLoading ? (
                            <tr><td colSpan={(orcGix?.competencias?.length || 12) + 4} className="text-center py-12 text-gray-400">
                              <RefreshCw className="w-8 h-8 mx-auto mb-3 opacity-40 animate-spin" />
                              <p className="text-sm">Consultando o GIX…</p>
                            </td></tr>
                          ) : !orcGix || orcItensFiltrados.length === 0 ? (
                            <tr><td colSpan={(orcGix?.competencias?.length || 12) + 4} className="text-center py-12 text-gray-400">
                              <Calculator className="w-10 h-10 mx-auto mb-3 opacity-40" />
                              <p className="text-sm font-medium">Nenhum orçamento lançado neste escopo</p>
                              <p className="text-xs mt-1">Tente outro ano, ou troque entre Grupo e Empresa</p>
                            </td></tr>
                          ) : (
                            orcItensFiltrados.map(item => (
                              <tr key={item.codigo} className="hover:bg-gray-50 transition-colors">
                                <td className="px-5 py-3 text-sm font-medium text-gray-900 sticky left-0 bg-white hover:bg-gray-50 z-10">
                                  {item.despesa}
                                </td>
                                <td className="px-4 py-2.5 text-right whitespace-nowrap align-top"
                                    title={item.titulos ? `${item.titulos} título(s) pago(s)` : undefined}>
                                  {orcTitulos && (
                                    item.realizado_total ? (
                                      <button onClick={() => abrirDrill('titulos', item.codigo, item.despesa)}
                                        className="block w-full text-right text-sm font-semibold text-green-700 leading-tight hover:underline"
                                        title="Ver todos os títulos do período">
                                        {formatarMoeda(item.realizado_total)}
                                      </button>
                                    ) : <div className="text-sm text-gray-300 leading-tight">—</div>
                                  )}
                                  {orcOcAberto && !!item.oc_total && (
                                    <button onClick={() => abrirDrill('oc', item.codigo, item.despesa)}
                                      className="block w-full text-right text-xs text-amber-600 leading-tight mt-0.5 hover:underline"
                                      title={`${item.ocs || 0} OC(s) em aberto — clique para ver`}>
                                      +{formatarMoeda(item.oc_total)} OC
                                    </button>
                                  )}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {(() => {
                                    const consumido = (orcTitulos ? (item.realizado_total || 0) : 0)
                                      + (orcOcAberto ? (item.oc_total || 0) : 0)
                                      + (orcOcAberto && !orcOcSoLiberadas ? (item.oc_nlib_total || 0) : 0);
                                    if (!item.total) return <span className="block text-center text-xs text-gray-300">—</span>;
                                    const pct = Math.round(100 * consumido / item.total);
                                    return (
                                      <div className="flex items-center gap-2 justify-center">
                                        <div className="w-14 h-2 bg-gray-200 rounded-full overflow-hidden">
                                          <div className={`h-full rounded-full ${
                                            pct > 100 ? 'bg-red-500' : pct > 80 ? 'bg-yellow-500' : 'bg-green-500'}`}
                                            style={{ width: `${Math.min(pct, 100)}%` }} />
                                        </div>
                                        <span className={`text-xs font-semibold ${
                                          pct > 100 ? 'text-red-600' : pct > 80 ? 'text-yellow-700' : 'text-gray-600'}`}>
                                          {pct}%
                                        </span>
                                      </div>
                                    );
                                  })()}
                                </td>
                                <td className="px-4 py-3 text-sm font-bold text-right text-blue-700 whitespace-nowrap">
                                  {item.total ? formatarMoeda(item.total) : '—'}
                                </td>
                                {orcGix.competencias.map(c => {
                                  const orcadoMes = item.meses[c] || 0;
                                  const realMes = orcTitulos ? (item.realizado_meses?.[c] || 0) : 0;
                                  const ocMes = orcOcAberto ? (item.oc_meses?.[c] || 0) : 0;
                                  const ocNlibMes = orcOcAberto ? (item.oc_nlib_meses?.[c] || 0) : 0;
                                  // estourou somando títulos + a OC que conta no total
                                  const comprometido = ocMes + (orcOcSoLiberadas ? 0 : ocNlibMes);
                                  const estourou = !!orcadoMes && (realMes + comprometido) > orcadoMes;
                                  if (!orcadoMes && !realMes && !ocMes && !ocNlibMes) {
                                    return <td key={c} className="px-4 py-3 text-sm text-right text-gray-300 whitespace-nowrap">—</td>;
                                  }
                                  return (
                                    <td key={c} className="px-4 py-2.5 text-right whitespace-nowrap align-top">
                                      <div className="text-sm text-gray-700 leading-tight">
                                        {orcadoMes ? formatarMoeda(orcadoMes) : '—'}
                                      </div>
                                      {orcTitulos && (
                                        realMes ? (
                                          <button onClick={() => abrirDrill('titulos', item.codigo, item.despesa, c)}
                                            className={`block w-full text-right text-xs leading-tight mt-0.5 hover:underline ${estourou ? 'text-red-600 font-semibold' : 'text-green-700'}`}
                                            title="Ver os títulos que formam este valor">
                                            {formatarMoeda(realMes)}
                                          </button>
                                        ) : <div className="text-xs leading-tight mt-0.5 text-gray-300">—</div>
                                      )}
                                      {orcOcAberto && !!(ocMes || ocNlibMes) && (
                                        <button onClick={() => abrirDrill('oc', item.codigo, item.despesa, c)}
                                          className="block w-full text-right hover:underline"
                                          title="Ver as ordens de compra deste mês">
                                          {!!ocMes && <span className="block text-xs leading-tight mt-0.5 text-amber-600">+{formatarMoeda(ocMes)}</span>}
                                          {!!ocNlibMes && <span className="block text-[11px] leading-tight mt-0.5 text-amber-400">({formatarMoeda(ocNlibMes)})</span>}
                                        </button>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))
                          )}
                        </tbody>
                        {orcGix && orcItensFiltrados.length > 0 && (
                          <tfoot>
                            <tr className="bg-gray-50 border-t-2 border-gray-200 font-bold">
                              <td className="px-5 py-3 text-sm text-gray-900 sticky left-0 bg-gray-50 z-10">TOTAL</td>
                              <td className="px-4 py-3 text-sm text-right text-green-700 whitespace-nowrap">{formatarMoeda(orcGix.realizado_total_geral || 0)}</td>
                              <td className="px-4 py-3 text-sm text-center text-gray-600 whitespace-nowrap">
                                {orcGix.total_geral
                                  ? `${Math.round(100 * ((orcTitulos ? (orcGix.realizado_total_geral || 0) : 0)
                                      + (orcOcAberto ? (orcGix.oc_total_geral || 0) : 0)
                                      + (orcOcAberto && !orcOcSoLiberadas ? (orcGix.oc_nlib_total_geral || 0) : 0)) / orcGix.total_geral)}%`
                                  : '—'}
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-blue-700 whitespace-nowrap">{formatarMoeda(orcGix.total_geral)}</td>
                              {orcGix.competencias.map(c => {
                                const somaOrc = orcItensFiltrados.reduce((s, i) => s + (i.meses[c] || 0), 0);
                                const somaReal = orcTitulos
                                  ? orcItensFiltrados.reduce((s, i) => s + (i.realizado_meses?.[c] || 0), 0) : 0;
                                const somaOc = orcOcAberto
                                  ? orcItensFiltrados.reduce((s, i) => s + (i.oc_meses?.[c] || 0), 0) : 0;
                                return (
                                  <td key={c} className="px-4 py-2.5 text-right whitespace-nowrap align-top">
                                    <div className="text-sm text-gray-700 leading-tight">{somaOrc ? formatarMoeda(somaOrc) : '—'}</div>
                                    {orcTitulos && (
                                      <div className={`text-xs leading-tight mt-0.5 font-semibold ${
                                        somaOrc && (somaReal + somaOc) > somaOrc ? 'text-red-600' : 'text-green-700'}`}>
                                        {somaReal ? formatarMoeda(somaReal) : '—'}
                                      </div>
                                    )}
                                    {orcOcAberto && !!somaOc && (
                                      <div className="text-xs leading-tight mt-0.5 text-amber-600">+{formatarMoeda(somaOc)}</div>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          </tfoot>
                        )}
                      </table>
                    </div>
                  </div>

                  <p className="text-xs text-gray-400 px-1 leading-relaxed">
                    Somente leitura. <strong>Orçado</strong> = tabela <code className="bg-gray-100 px-1 rounded">arqocpg</code> do
                    GIX (os valores por mês são os mesmos da tela "Carregar Orçamentos").
                    {' '}<strong>Títulos a pagar</strong> = <code className="bg-gray-100 px-1 rounded">arqcpag + arqapag</code>,
                    pela data de vencimento.
                    {orcOcAberto && <> <strong>OC em aberto</strong> = parcelas de <code className="bg-gray-100 px-1 rounded">mov_ordcvc</code> (status
                    ABER/ENTP), pelo vencimento da parcela; entre parênteses, as ainda não liberadas.</>}
                    {' '}Em cada mês a linha de cima é o orçado — vermelho quando estoura.
                    Conferido contra a tela do GIX (out/2026): orçado, valor, OC liberada, OC não liberada,
                    despesas+OC e diferença batem ao centavo.
                    {!!orcGix?.realizado_fora_orcamento && (
                      <> Os {formatarMoeda(orcGix.realizado_fora_orcamento)} fora do orçamento são pagamentos
                      de tipos de despesa que não têm linha orçada neste escopo.</>
                    )}
                    {' '}Clique em qualquer valor de título ou OC para ver os documentos por trás dele.
                  </p>

                  {/* ═══ Drill-down: os documentos por trás do número ═══ */}
                  {orcDrill && (
                    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
                      onClick={() => setOrcDrill(null)}>
                      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col"
                        onClick={e => e.stopPropagation()}>
                        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200">
                          <div>
                            <h3 className="font-bold text-gray-900">
                              {orcDrill.tipo === 'titulos' ? 'Títulos a pagar' : 'Ordens de compra em aberto'}
                            </h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {orcDrill.despesa}
                              {orcDrill.competencia ? ` · ${rotuloCompetencia(orcDrill.competencia)}` : ` · ${orcAno}`}
                              {orcDrillData?.linhas ? ` · ${orcDrillData.linhas.length} documento(s)` : ''}
                            </p>
                          </div>
                          <button onClick={() => setOrcDrill(null)}
                            className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400"><X className="w-5 h-5" /></button>
                        </div>

                        <div className="overflow-auto flex-1">
                          {orcDrillLoading ? (
                            <div className="py-16 text-center text-gray-400">
                              <RefreshCw className="w-7 h-7 mx-auto mb-3 opacity-40 animate-spin" />
                              <p className="text-sm">Consultando o GIX…</p>
                            </div>
                          ) : orcDrillData?.erro ? (
                            <div className="py-16 text-center text-red-500 text-sm">{orcDrillData.erro}</div>
                          ) : !orcDrillData?.linhas?.length ? (
                            <div className="py-16 text-center text-gray-400 text-sm">Nenhum documento encontrado</div>
                          ) : (
                            <table className="w-full text-sm">
                              <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                                <tr>
                                  <th className="text-left px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase">Documento</th>
                                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Fornecedor</th>
                                  <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Emp.</th>
                                  <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">Vencimento</th>
                                  <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase">
                                    {orcDrill.tipo === 'oc' ? 'Situação' : 'Origem'}
                                  </th>
                                  <th className="text-right px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase">Valor</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {orcDrillData.linhas.map((l: any, idx: number) => (
                                  <tr key={idx} className="hover:bg-gray-50">
                                    <td className="px-5 py-2 font-medium text-gray-900">
                                      {l.documento}{l.parcela ? `/${l.parcela}` : ''}
                                    </td>
                                    <td className="px-4 py-2 text-gray-600">{l.nome || l.fornecedor || '-'}</td>
                                    <td className="px-4 py-2 text-center text-gray-500">{l.empresa}</td>
                                    <td className="px-4 py-2 text-center text-gray-600">
                                      {l.vencimento ? formatarData(l.vencimento) : '-'}
                                    </td>
                                    <td className="px-4 py-2 text-center">
                                      {orcDrill.tipo === 'oc' ? (
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                                          l.liberada === 'S' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
                                          {l.liberada === 'S' ? 'liberada' : 'não liberada'} · {l.status}
                                        </span>
                                      ) : (
                                        <span className="text-xs text-gray-400">{l.origem}</span>
                                      )}
                                    </td>
                                    <td className="px-5 py-2 text-right font-semibold text-gray-800">
                                      {formatarMoeda(Number(l.valor) || 0)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>

                        <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50 rounded-b-2xl">
                          <span className="text-xs text-gray-500">
                            {orcDrillData?.truncado ? 'Mostrando as primeiras 300 linhas. ' : ''}
                            {orcDrill.tipo === 'oc'
                              ? 'Valor pendente das parcelas, liberadas e não liberadas.'
                              : 'Títulos pela data de vencimento.'}
                          </span>
                          <span className="text-sm font-bold text-gray-900">
                            {formatarMoeda(orcDrillData?.total || 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ═══════════════ LINHAS MANUAIS (Mongo) ═══════════════ */}
              {orcFonte === 'manual' && (
              <div className="space-y-4">
              {/* Resumo cards */}
              {orcamentos.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-2xl border border-gray-200 p-5">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Total Orçado</p>
                    <p className="text-2xl font-bold text-blue-700">{formatarMoeda(orcamentos.reduce((s, o) => s + (o.valorOrcado || 0), 0))}</p>
                  </div>
                  <div className="bg-white rounded-2xl border border-gray-200 p-5">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Total Realizado</p>
                    <p className="text-2xl font-bold text-green-700">{formatarMoeda(orcamentos.reduce((s, o) => s + (o.valorRealizado || 0), 0))}</p>
                  </div>
                  <div className="bg-white rounded-2xl border border-gray-200 p-5">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Saldo (Orçado - Realizado)</p>
                    {(() => {
                      const saldo = orcamentos.reduce((s, o) => s + (o.valorOrcado || 0), 0) - orcamentos.reduce((s, o) => s + (o.valorRealizado || 0), 0);
                      return <p className={`text-2xl font-bold ${saldo >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatarMoeda(saldo)}</p>;
                    })()}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type="text" value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar por categoria ou centro de custo..."
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Categoria</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Centro de Custo</th>
                      <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Período</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Orçado</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Realizado</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Saldo</th>
                      <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">% Exec.</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {orcamentos
                      .filter(o => !busca || o.categoria?.toLowerCase().includes(busca.toLowerCase()) || o.centroCusto?.toLowerCase().includes(busca.toLowerCase()))
                      .length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center py-12 text-gray-400">
                          <Calculator className="w-10 h-10 mx-auto mb-3 opacity-40" />
                          <p className="text-sm font-medium">Nenhuma linha orçamentária</p>
                          <p className="text-xs mt-1">Clique em "Nova Linha Orçamentária" para adicionar</p>
                        </td>
                      </tr>
                    ) : (
                      orcamentos
                        .filter(o => !busca || o.categoria?.toLowerCase().includes(busca.toLowerCase()) || o.centroCusto?.toLowerCase().includes(busca.toLowerCase()))
                        .map(orc => {
                          const saldo = (orc.valorOrcado || 0) - (orc.valorRealizado || 0);
                          const pctExec = orc.valorOrcado ? Math.round((orc.valorRealizado / orc.valorOrcado) * 100) : 0;
                          const meses = ['', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
                          return (
                            <tr key={orc._id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-5 py-3 text-sm font-medium text-gray-900">{orc.categoria}</td>
                              <td className="px-5 py-3 text-sm text-gray-600">{orc.centroCusto || '-'}</td>
                              <td className="px-5 py-3 text-sm text-gray-600 text-center">{meses[orc.mes] || orc.mes}/{orc.ano}</td>
                              <td className="px-5 py-3 text-sm font-semibold text-right text-blue-700">{formatarMoeda(orc.valorOrcado || 0)}</td>
                              <td className="px-5 py-3 text-sm font-semibold text-right text-green-700">{formatarMoeda(orc.valorRealizado || 0)}</td>
                              <td className="px-5 py-3 text-sm font-semibold text-right">
                                <span className={saldo >= 0 ? 'text-green-600' : 'text-red-600'}>{formatarMoeda(saldo)}</span>
                              </td>
                              <td className="px-5 py-3 text-center">
                                <div className="flex items-center gap-2 justify-center">
                                  <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div className={`h-full rounded-full transition-all ${pctExec > 100 ? 'bg-red-500' : pctExec > 80 ? 'bg-yellow-500' : 'bg-green-500'}`}
                                      style={{ width: `${Math.min(pctExec, 100)}%` }} />
                                  </div>
                                  <span className={`text-xs font-semibold ${pctExec > 100 ? 'text-red-600' : 'text-gray-600'}`}>{pctExec}%</span>
                                </div>
                              </td>
                              <td className="px-5 py-3">
                                <div className="flex items-center justify-end gap-1">
                                  <button onClick={() => apiGenericDelete('orcamentos', orc._id, carregarOrcamentos)}
                                    className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors" title="Excluir"><Trash2 className="w-4 h-4" /></button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                    )}
                  </tbody>
                </table>
              </div>
              </div>
              )}
            </div>
          )}

          {/* ═══ CAIXAS E BANCOS ═══ */}
          {currentSection === 'caixas-bancos' && (
            <div className="space-y-4">
              {/* Resumo Saldos */}
              {contasBancarias.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-2xl border border-gray-200 p-5">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Total em Contas</p>
                    <p className="text-2xl font-bold text-blue-700">{formatarMoeda(contasBancarias.filter(c => c.ativo !== false).reduce((s, c) => s + (c.saldoAtual || 0), 0))}</p>
                  </div>
                  <div className="bg-white rounded-2xl border border-gray-200 p-5">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Contas Correntes</p>
                    <p className="text-2xl font-bold text-green-700">{formatarMoeda(contasBancarias.filter(c => c.tipo === 'corrente' && c.ativo !== false).reduce((s, c) => s + (c.saldoAtual || 0), 0))}</p>
                  </div>
                  <div className="bg-white rounded-2xl border border-gray-200 p-5">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Poupança</p>
                    <p className="text-2xl font-bold text-purple-700">{formatarMoeda(contasBancarias.filter(c => c.tipo === 'poupanca' && c.ativo !== false).reduce((s, c) => s + (c.saldoAtual || 0), 0))}</p>
                  </div>
                  <div className="bg-white rounded-2xl border border-gray-200 p-5">
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Caixa</p>
                    <p className="text-2xl font-bold text-amber-700">{formatarMoeda(contasBancarias.filter(c => c.tipo === 'caixa' && c.ativo !== false).reduce((s, c) => s + (c.saldoAtual || 0), 0))}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type="text" value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar banco, agência, conta..."
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
                </div>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Banco</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Apelido</th>
                      <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Agência</th>
                      <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Conta</th>
                      <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Tipo</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Saldo Atual</th>
                      <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {contasBancarias
                      .filter(c => !busca || c.banco?.toLowerCase().includes(busca.toLowerCase()) || c.apelido?.toLowerCase().includes(busca.toLowerCase()) || c.conta?.toLowerCase().includes(busca.toLowerCase()))
                      .length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center py-12 text-gray-400">
                          <Landmark className="w-10 h-10 mx-auto mb-3 opacity-40" />
                          <p className="text-sm font-medium">Nenhuma conta bancária cadastrada</p>
                          <p className="text-xs mt-1">Clique em "Nova Conta Bancária" para adicionar</p>
                        </td>
                      </tr>
                    ) : (
                      contasBancarias
                        .filter(c => !busca || c.banco?.toLowerCase().includes(busca.toLowerCase()) || c.apelido?.toLowerCase().includes(busca.toLowerCase()) || c.conta?.toLowerCase().includes(busca.toLowerCase()))
                        .map(cb => {
                          const tipoLabels: Record<string, string> = { corrente: 'Corrente', poupanca: 'Poupança', investimento: 'Investimento', caixa: 'Caixa' };
                          const tipoColors: Record<string, string> = { corrente: 'bg-blue-100 text-blue-700', poupanca: 'bg-purple-100 text-purple-700', investimento: 'bg-green-100 text-green-700', caixa: 'bg-amber-100 text-amber-700' };
                          return (
                            <tr key={cb._id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-5 py-3">
                                <div className="flex items-center gap-2">
                                  <Landmark className="w-4 h-4 text-blue-500" />
                                  <span className="text-sm font-medium text-gray-900">{cb.banco}</span>
                                </div>
                              </td>
                              <td className="px-5 py-3 text-sm text-gray-600">{cb.apelido || '-'}</td>
                              <td className="px-5 py-3 text-sm text-gray-600 text-center font-mono">{cb.agencia || '-'}</td>
                              <td className="px-5 py-3 text-sm text-gray-600 text-center font-mono">{cb.conta || '-'}</td>
                              <td className="px-5 py-3 text-center">
                                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${tipoColors[cb.tipo] || 'bg-gray-100 text-gray-700'}`}>
                                  {tipoLabels[cb.tipo] || cb.tipo}
                                </span>
                              </td>
                              <td className="px-5 py-3 text-right">
                                <span className={`text-sm font-bold ${(cb.saldoAtual || 0) >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                                  {formatarMoeda(cb.saldoAtual || 0)}
                                </span>
                              </td>
                              <td className="px-5 py-3 text-center">
                                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${cb.ativo !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                                  {cb.ativo !== false ? 'Ativa' : 'Inativa'}
                                </span>
                              </td>
                              <td className="px-5 py-3">
                                <div className="flex items-center justify-end gap-1">
                                  <button onClick={() => apiGenericDelete('contas-bancarias', cb._id, carregarContasBancarias)}
                                    className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors" title="Excluir"><Trash2 className="w-4 h-4" /></button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ═══ COMPRAS ═══ */}
          {currentSection === 'compras' && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input type="text" value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar por fornecedor ou descrição..."
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
                </div>
                <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}
                  className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400">
                  <option value="todos">Todos</option>
                  <option value="rascunho">Rascunho</option>
                  <option value="aprovado">Aprovado</option>
                  <option value="pedido">Pedido</option>
                  <option value="recebido">Recebido</option>
                  <option value="cancelado">Cancelado</option>
                </select>
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Nº</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Fornecedor</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Descrição</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Data Compra</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Entrega</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Valor Total</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {compras.filter(c => {
                      if (filtroStatus !== 'todos' && c.status !== filtroStatus) return false;
                      if (busca && !c.fornecedor.toLowerCase().includes(busca.toLowerCase()) && !c.descricao.toLowerCase().includes(busca.toLowerCase())) return false;
                      return true;
                    }).length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center py-12 text-gray-400">
                          <ShoppingCart className="w-10 h-10 mx-auto mb-3 opacity-40" />
                          <p className="text-sm font-medium">Nenhuma compra encontrada</p>
                          <p className="text-xs mt-1">Clique em "Nova Compra" para adicionar</p>
                        </td>
                      </tr>
                    ) : (
                      compras.filter(c => {
                        if (filtroStatus !== 'todos' && c.status !== filtroStatus) return false;
                        if (busca && !c.fornecedor.toLowerCase().includes(busca.toLowerCase()) && !c.descricao.toLowerCase().includes(busca.toLowerCase())) return false;
                        return true;
                      }).map(compra => (
                        <tr key={compra._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-5 py-3 text-sm font-mono font-bold text-blue-600">#{String(compra.numero).padStart(4, '0')}</td>
                          <td className="px-5 py-3">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getCompraStatusColor(compra.status)}`}>
                              {compra.status}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-sm font-medium text-gray-900">{compra.fornecedor || '-'}</td>
                          <td className="px-5 py-3 text-sm text-gray-600 max-w-[200px] truncate">{compra.descricao || '-'}</td>
                          <td className="px-5 py-3 text-sm text-gray-600">{compra.dataCompra ? formatarData(compra.dataCompra) : '-'}</td>
                          <td className="px-5 py-3 text-sm text-gray-600">{compra.dataEntrega ? formatarData(compra.dataEntrega) : '-'}</td>
                          <td className="px-5 py-3 text-sm font-bold text-right text-blue-700">{formatarMoeda(compra.valorTotal)}</td>
                          <td className="px-5 py-3">
                            <div className="flex items-center justify-end gap-1">
                              {compra.status === 'rascunho' && (
                                <button onClick={() => { 
                                  financeFetch(`/api/finance/compras/${encodeURIComponent(compra._id)}`, { method: 'PUT', headers: {'Content-Type':'application/json'}, body: JSON.stringify({status:'aprovado'}) }).then(() => carregarCompras());
                                }} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors" title="Aprovar">
                                  <CheckCircle className="w-4 h-4" />
                                </button>
                              )}
                              {compra.status === 'aprovado' && (
                                <button onClick={() => {
                                  financeFetch(`/api/finance/compras/${encodeURIComponent(compra._id)}`, { method: 'PUT', headers: {'Content-Type':'application/json'}, body: JSON.stringify({status:'pedido'}) }).then(() => carregarCompras());
                                }} className="p-1.5 text-yellow-500 hover:bg-yellow-50 rounded-lg transition-colors" title="Marcar como Pedido">
                                  <Truck className="w-4 h-4" />
                                </button>
                              )}
                              {compra.status === 'pedido' && (
                                <button onClick={() => {
                                  financeFetch(`/api/finance/compras/${encodeURIComponent(compra._id)}`, { method: 'PUT', headers: {'Content-Type':'application/json'}, body: JSON.stringify({status:'recebido'}) }).then(() => carregarCompras());
                                }} className="p-1.5 text-green-500 hover:bg-green-50 rounded-lg transition-colors" title="Marcar como Recebido">
                                  <Package className="w-4 h-4" />
                                </button>
                              )}
                              <button onClick={() => apiGenericDelete('compras', compra._id, carregarCompras)}
                                className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors" title="Excluir">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ═══ FORNECEDORES ═══ */}
          {currentSection === 'fornecedores' && (
            <div className="space-y-4">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar fornecedor..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Razão Social</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Nome Fantasia</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">CNPJ</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Contato</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Cidade/UF</th>
                      <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {fornecedores.filter(f => !busca || f.razaoSocial.toLowerCase().includes(busca.toLowerCase()) || f.nomeFantasia.toLowerCase().includes(busca.toLowerCase()) || f.cnpj.includes(busca)).length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-12 text-gray-400">
                          <Truck className="w-10 h-10 mx-auto mb-3 opacity-40" />
                          <p className="text-sm font-medium">Nenhum fornecedor cadastrado</p>
                          <p className="text-xs mt-1">Clique em "Novo Fornecedor" para adicionar</p>
                        </td>
                      </tr>
                    ) : (
                      fornecedores.filter(f => !busca || f.razaoSocial.toLowerCase().includes(busca.toLowerCase()) || f.nomeFantasia.toLowerCase().includes(busca.toLowerCase()) || f.cnpj.includes(busca)).map(forn => (
                        <tr key={forn._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-5 py-3 text-sm font-medium text-gray-900">{forn.razaoSocial}</td>
                          <td className="px-5 py-3 text-sm text-gray-600">{forn.nomeFantasia || '-'}</td>
                          <td className="px-5 py-3 text-sm font-mono text-gray-600">{forn.cnpj || '-'}</td>
                          <td className="px-5 py-3 text-sm text-gray-600">
                            <div>{forn.contato || '-'}</div>
                            <div className="text-xs text-gray-400">{forn.email}</div>
                          </td>
                          <td className="px-5 py-3 text-sm text-gray-600">{forn.cidade ? `${forn.cidade}/${forn.estado}` : '-'}</td>
                          <td className="px-5 py-3 text-center">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${forn.ativo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              {forn.ativo ? 'Ativo' : 'Inativo'}
                            </span>
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => apiGenericDelete('fornecedores', forn._id, carregarFornecedores)}
                                className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors" title="Excluir"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ═══ COMPRADORES ═══ */}
          {currentSection === 'compradores' && (
            <div className="space-y-4">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar comprador..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Nome</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">E-mail</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Departamento</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Limite Aprovação</th>
                      <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {compradores.filter(c => !busca || c.nome.toLowerCase().includes(busca.toLowerCase()) || c.email.toLowerCase().includes(busca.toLowerCase())).length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-12 text-gray-400">
                          <UserCheck className="w-10 h-10 mx-auto mb-3 opacity-40" />
                          <p className="text-sm font-medium">Nenhum comprador cadastrado</p>
                          <p className="text-xs mt-1">Clique em "Novo Comprador" para adicionar</p>
                        </td>
                      </tr>
                    ) : (
                      compradores.filter(c => !busca || c.nome.toLowerCase().includes(busca.toLowerCase()) || c.email.toLowerCase().includes(busca.toLowerCase())).map(comp => (
                        <tr key={comp._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-5 py-3 text-sm font-medium text-gray-900">{comp.nome}</td>
                          <td className="px-5 py-3 text-sm text-gray-600">{comp.email || '-'}</td>
                          <td className="px-5 py-3 text-sm text-gray-600">{comp.departamento || '-'}</td>
                          <td className="px-5 py-3 text-sm font-semibold text-right text-blue-700">{formatarMoeda(comp.limiteAprovacao || 0)}</td>
                          <td className="px-5 py-3 text-center">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${comp.ativo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              {comp.ativo ? 'Ativo' : 'Inativo'}
                            </span>
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => apiGenericDelete('compradores', comp._id, carregarCompradores)}
                                className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors" title="Excluir"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ═══ PRODUTOS ═══ */}
          {currentSection === 'produtos' && (
            <div className="space-y-4">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar produto..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Código</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Nome</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Categoria</th>
                      <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Unidade</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Preço Compra</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Preço Venda</th>
                      <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {produtos.filter(p => !busca || p.nome.toLowerCase().includes(busca.toLowerCase()) || p.codigo.toLowerCase().includes(busca.toLowerCase())).length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center py-12 text-gray-400">
                          <PackageSearch className="w-10 h-10 mx-auto mb-3 opacity-40" />
                          <p className="text-sm font-medium">Nenhum produto cadastrado</p>
                          <p className="text-xs mt-1">Clique em "Novo Produto" para adicionar</p>
                        </td>
                      </tr>
                    ) : (
                      produtos.filter(p => !busca || p.nome.toLowerCase().includes(busca.toLowerCase()) || p.codigo.toLowerCase().includes(busca.toLowerCase())).map(prod => (
                        <tr key={prod._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-5 py-3 text-sm font-mono text-blue-600 font-semibold">{prod.codigo}</td>
                          <td className="px-5 py-3 text-sm font-medium text-gray-900">{prod.nome}</td>
                          <td className="px-5 py-3 text-sm text-gray-600">{prod.categoria || '-'}</td>
                          <td className="px-5 py-3 text-sm text-gray-600 text-center">{prod.unidade || 'UN'}</td>
                          <td className="px-5 py-3 text-sm font-semibold text-right text-blue-700">{formatarMoeda(prod.precoCompra || 0)}</td>
                          <td className="px-5 py-3 text-sm font-semibold text-right text-green-700">{formatarMoeda(prod.precoVenda || 0)}</td>
                          <td className="px-5 py-3 text-center">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${prod.ativo ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                              {prod.ativo ? 'Ativo' : 'Inativo'}
                            </span>
                          </td>
                          <td className="px-5 py-3">
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => apiGenericDelete('produtos', prod._id, carregarProdutos)}
                                className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors" title="Excluir"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ═══ ESTOQUE ═══ */}
          {currentSection === 'estoque' && (
            <div className="space-y-4">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar item do estoque..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Código</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Nome</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Categoria</th>
                      <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Unid.</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Qtd. Atual</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Qtd. Mínima</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Localização</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Custo Médio</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {itensEstoque.filter(i => !busca || i.nome.toLowerCase().includes(busca.toLowerCase()) || i.codigo.toLowerCase().includes(busca.toLowerCase())).length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center py-12 text-gray-400">
                          <Boxes className="w-10 h-10 mx-auto mb-3 opacity-40" />
                          <p className="text-sm font-medium">Nenhum item no estoque</p>
                          <p className="text-xs mt-1">Clique em "Novo Item" para adicionar</p>
                        </td>
                      </tr>
                    ) : (
                      itensEstoque.filter(i => !busca || i.nome.toLowerCase().includes(busca.toLowerCase()) || i.codigo.toLowerCase().includes(busca.toLowerCase())).map(item => (
                        <tr key={item._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-5 py-3 text-sm font-mono text-blue-600 font-semibold">{item.codigo}</td>
                          <td className="px-5 py-3 text-sm font-medium text-gray-900">{item.nome}</td>
                          <td className="px-5 py-3 text-sm text-gray-600">{item.categoria || '-'}</td>
                          <td className="px-5 py-3 text-sm text-gray-600 text-center">{item.unidade || 'UN'}</td>
                          <td className="px-5 py-3 text-right">
                            <span className={`text-sm font-semibold ${item.quantidadeAtual <= item.quantidadeMinima ? 'text-red-600' : 'text-gray-900'}`}>
                              {item.quantidadeAtual}
                            </span>
                            {item.quantidadeAtual <= item.quantidadeMinima && (
                              <AlertTriangle className="inline w-3.5 h-3.5 text-red-500 ml-1" />
                            )}
                          </td>
                          <td className="px-5 py-3 text-sm text-gray-600 text-right">{item.quantidadeMinima}</td>
                          <td className="px-5 py-3 text-sm text-gray-600">{item.localizacao || '-'}</td>
                          <td className="px-5 py-3 text-sm font-semibold text-right text-blue-700">{formatarMoeda(item.custoMedio || 0)}</td>
                          <td className="px-5 py-3">
                            <div className="flex items-center justify-end gap-1">
                              <button onClick={() => apiGenericDelete('estoque', item._id, carregarEstoque)}
                                className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors" title="Excluir"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ═══ INVENTÁRIO ═══ */}
          {currentSection === 'inventario' && (
            <div className="space-y-4">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar inventário..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
              </div>
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Nº</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Descrição</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Responsável</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Data Início</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Data Fim</th>
                      <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                      <th className="text-center px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Itens</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500 uppercase">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {inventarios.filter(i => !busca || i.descricao.toLowerCase().includes(busca.toLowerCase()) || i.responsavel.toLowerCase().includes(busca.toLowerCase())).length === 0 ? (
                      <tr>
                        <td colSpan={8} className="text-center py-12 text-gray-400">
                          <ClipboardList className="w-10 h-10 mx-auto mb-3 opacity-40" />
                          <p className="text-sm font-medium">Nenhum inventário cadastrado</p>
                          <p className="text-xs mt-1">Clique em "Novo Inventário" para adicionar</p>
                        </td>
                      </tr>
                    ) : (
                      inventarios.filter(i => !busca || i.descricao.toLowerCase().includes(busca.toLowerCase()) || i.responsavel.toLowerCase().includes(busca.toLowerCase())).map(inv => (
                        <tr key={inv._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-5 py-3 text-sm font-mono font-bold text-blue-600">#{String(inv.numero).padStart(4, '0')}</td>
                          <td className="px-5 py-3 text-sm font-medium text-gray-900">{inv.descricao}</td>
                          <td className="px-5 py-3 text-sm text-gray-600">{inv.responsavel || '-'}</td>
                          <td className="px-5 py-3 text-sm text-gray-600">{inv.dataInicio ? formatarData(inv.dataInicio) : '-'}</td>
                          <td className="px-5 py-3 text-sm text-gray-600">{inv.dataFim ? formatarData(inv.dataFim) : '-'}</td>
                          <td className="px-5 py-3 text-center">
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                              inv.status === 'em_andamento' ? 'bg-yellow-100 text-yellow-700' :
                              inv.status === 'concluido' ? 'bg-green-100 text-green-700' :
                              inv.status === 'cancelado' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {inv.status === 'em_andamento' ? 'Em Andamento' : inv.status === 'concluido' ? 'Concluído' : inv.status === 'cancelado' ? 'Cancelado' : 'Planejado'}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-center text-sm font-semibold text-gray-700">{inv.itens?.length || 0}</td>
                          <td className="px-5 py-3">
                            <div className="flex items-center justify-end gap-1">
                              {inv.status === 'planejado' && (
                                <button onClick={() => {
                                  financeFetch(`/api/finance/inventarios/${encodeURIComponent(inv._id)}`, { method: 'PUT', headers: {'Content-Type':'application/json'}, body: JSON.stringify({status:'em_andamento'}) }).then(() => carregarInventarios());
                                }} className="p-1.5 text-yellow-500 hover:bg-yellow-50 rounded-lg transition-colors" title="Iniciar">
                                  <ListChecks className="w-4 h-4" />
                                </button>
                              )}
                              {inv.status === 'em_andamento' && (
                                <button onClick={() => {
                                  financeFetch(`/api/finance/inventarios/${encodeURIComponent(inv._id)}`, { method: 'PUT', headers: {'Content-Type':'application/json'}, body: JSON.stringify({status:'concluido'}) }).then(() => carregarInventarios());
                                }} className="p-1.5 text-green-500 hover:bg-green-50 rounded-lg transition-colors" title="Concluir">
                                  <CheckCircle className="w-4 h-4" />
                                </button>
                              )}
                              <button onClick={() => apiGenericDelete('inventarios', inv._id, carregarInventarios)}
                                className="p-1.5 text-red-400 hover:bg-red-50 rounded-lg transition-colors" title="Excluir"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ═══ CONFIGURAÇÕES ═══ */}
          {currentSection === 'configuracoes' && (
            <div className="space-y-6">
              {/* Moedas: base do sistema + quais ficam disponíveis nos lançamentos */}
              <MoedasConfig config={config} onSalvar={salvarConfig} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Config de Módulos */}
                <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Settings className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Configuração de Módulos</h3>
                      <p className="text-xs text-gray-500">Ative ou desative módulos e funcionalidades do ERP</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {[
                      { name: 'Financeiro', icon: LayoutDashboard, color: 'blue', features: [
                        { name: 'Dashboard', desc: 'Visão geral com indicadores financeiros' },
                        { name: 'Contas a Pagar', desc: 'Gerenciar despesas e pagamentos' },
                        { name: 'Contas a Receber', desc: 'Gerenciar receitas e recebimentos' },
                        { name: 'Orçamento', desc: 'Planejamento e controle orçamentário' },
                        { name: 'Caixas e Bancos', desc: 'Contas bancárias e movimentações' },
                      ]},
                      { name: 'Contratos', icon: Handshake, color: 'indigo', features: [
                        { name: 'Contratos', desc: 'Contratos, parcelas previstas e contrapartidas' },
                        { name: 'Fontes de Recurso', desc: 'De qual bolso o dinheiro sai e entra' },
                        { name: 'Reembolsos', desc: 'Gastos do bolso pessoal a devolver' },
                      ]},
                      { name: 'Compras', icon: ShoppingCart, color: 'emerald', features: [
                        { name: 'Ordens de Compra', desc: 'Criar e acompanhar pedidos de compra' },
                        { name: 'Fornecedores', desc: 'Cadastro e gestão de fornecedores' },
                        { name: 'Compradores', desc: 'Cadastro de compradores responsáveis' },
                      ]},
                      { name: 'Estoque', icon: Boxes, color: 'amber', features: [
                        { name: 'Produtos', desc: 'Cadastro de produtos e materiais' },
                        { name: 'Controle de Estoque', desc: 'Entradas, saídas e saldo atual' },
                        { name: 'Inventário', desc: 'Contagem e ajuste de estoque físico' },
                      ]},
                      { name: 'Cadastros', icon: FileText, color: 'violet', features: [
                        { name: 'Categorias', desc: 'Classificação de receitas e despesas' },
                        { name: 'Centro de Custos', desc: 'Distribuição por departamentos' },
                        { name: 'Plano de Contas', desc: 'Estrutura contábil da empresa' },
                      ]},
                    ].map(mod => {
                      const ModIcon = mod.icon;
                      const modFeatures = modulesConfig[mod.name] || {};
                      const activeCount = Object.values(modFeatures).filter(v => v).length;
                      const allOn = activeCount === mod.features.length;
                      const someOn = activeCount > 0;
                      const colorMap: Record<string, { bg: string; text: string; activeBg: string }> = {
                        blue: { bg: 'bg-blue-50', text: 'text-blue-600', activeBg: 'bg-blue-600' },
                        indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600', activeBg: 'bg-indigo-600' },
                        emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', activeBg: 'bg-emerald-600' },
                        amber: { bg: 'bg-amber-50', text: 'text-amber-600', activeBg: 'bg-amber-600' },
                        violet: { bg: 'bg-violet-50', text: 'text-violet-600', activeBg: 'bg-violet-600' },
                      };
                      const colors = colorMap[mod.color] || colorMap.blue;
                      return (
                        <details key={mod.name} className="group rounded-xl border border-gray-100 overflow-hidden">
                          <summary className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 transition-colors list-none [&::-webkit-details-marker]:hidden">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center`}>
                                <ModIcon className={`w-4 h-4 ${colors.text}`} />
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-gray-900">{mod.name}</p>
                                <p className="text-[11px] text-gray-400">{activeCount}/{mod.features.length} ativas</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <ChevronDown className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-180" />
                              <button type="button" onClick={(e) => { e.preventDefault(); toggleModule(mod.name); }} className={`w-10 h-6 rounded-full flex items-center px-0.5 transition-colors ${allOn ? 'bg-blue-600 justify-end' : someOn ? 'bg-blue-400 justify-end' : 'bg-gray-300 justify-start'}`}>
                                <div className="w-5 h-5 rounded-full bg-white shadow-sm" />
                              </button>
                            </div>
                          </summary>
                          <div className="px-3 pb-3 pt-1">
                            <div className="space-y-1 ml-11">
                              {mod.features.map(feat => {
                                const isOn = modFeatures[feat.name] !== false;
                                return (
                                  <div key={feat.name} className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50 transition-colors">
                                    <div className="flex items-center gap-2.5">
                                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isOn ? colors.activeBg : 'bg-gray-300'}`} />
                                      <div>
                                        <p className={`text-xs font-medium ${isOn ? 'text-gray-700' : 'text-gray-400'}`}>{feat.name}</p>
                                        <p className="text-[11px] text-gray-400">{feat.desc}</p>
                                      </div>
                                    </div>
                                    <button type="button" onClick={() => toggleFeature(mod.name, feat.name)} className={`w-8 h-5 rounded-full flex items-center px-0.5 transition-colors flex-shrink-0 ${isOn ? colors.activeBg + ' justify-end' : 'bg-gray-300 justify-start'}`}>
                                      <div className="w-4 h-4 rounded-full bg-white shadow-sm" />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </details>
                      );
                    })}
                  </div>
                </div>

                {/* Cadastro de Usuários */}
                <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                      <Users className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Cadastro de Usuários</h3>
                      <p className="text-xs text-gray-500">Gerencie os usuários do ERP Financeiro</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-white text-xs font-bold">
                          {session.usuario.nome?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{session.usuario.nome}</p>
                          <p className="text-xs text-gray-500">{session.usuario.email}</p>
                        </div>
                      </div>
                      <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        {session.usuario.role === 'admin' ? 'Administrador' : 'Usuário'}
                      </span>
                    </div>
                  </div>
                  <button className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-xl text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors">
                    <Plus className="w-4 h-4" /> Convidar Usuário
                  </button>
                </div>
              </div>

              {/* Informações da Empresa */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Dados da Empresa</h3>
                    <p className="text-xs text-gray-500">Informações gerais da empresa</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">Nome da Empresa</p>
                    <p className="text-sm font-medium text-gray-900">{session.empresa?.nome || '-'}</p>
                  </div>
                  <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">ID da Empresa</p>
                    <p className="text-sm font-mono text-gray-600">{empresaId || '-'}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

        </main>
      </div>

      {/* ═══ MODAIS ═══ */}
      {showNovaConta && (
        <NovaContaModal tipo={showNovaConta} config={config} fontes={fontes}
          categorias={categorias.map(c => c.nome)} userEmail={session.usuario.email}
          onSave={(dados) => criarConta(showNovaConta, dados)}
          onClose={() => setShowNovaConta(null)} />
      )}

      {baixando && (
        <BaixaModal conta={baixando} config={config} fontes={fontes}
          onConfirm={(extra) => {
            atualizarStatus(baixando, baixando.tipo === 'pagar' ? 'pago' : 'recebido', extra);
            setBaixando(null);
          }}
          onClose={() => setBaixando(null)} />
      )}

      {showModal === 'fontes' && (
        <FonteModal fonte={fonteEditando} config={config} onSave={salvarFonte}
          onClose={() => { setShowModal(null); setFonteEditando(null); }} />
      )}

      {showModal === 'patrocinios' && (
        <PatrocinioModal patrocinio={patrocinioEditando} config={config} fontes={fontes}
          userEmail={session.usuario.email} onSave={salvarPatrocinio}
          onClose={() => { setShowModal(null); setPatrocinioEditando(null); }} />
      )}

      {showModal === 'categorias' && (
        <CadastroModal title="Nova Categoria" icon={<Tag className="w-5 h-5" />} color="blue" onClose={() => setShowModal(null)}
          fields={[
            { name: 'nome', label: 'Nome *', type: 'text', required: true, placeholder: 'Ex: Infraestrutura' },
            { name: 'tipo', label: 'Tipo', type: 'select', options: [
              { value: 'ambos', label: 'Ambos' }, { value: 'pagar', label: 'Pagar' }, { value: 'receber', label: 'Receber' }
            ]},
            { name: 'cor', label: 'Cor', type: 'color', defaultValue: '#3b82f6' },
          ]}
          onSave={(dados) => apiGenericCreate('categorias', dados, carregarCategorias)} />
      )}

      {showModal === 'centros-custo' && (
        <CadastroModal title="Novo Centro de Custo" icon={<Building2 className="w-5 h-5" />} color="blue" onClose={() => setShowModal(null)}
          fields={[
            { name: 'codigo', label: 'Código *', type: 'text', required: true, placeholder: 'Ex: CC001' },
            { name: 'nome', label: 'Nome *', type: 'text', required: true, placeholder: 'Ex: Departamento Comercial' },
            { name: 'descricao', label: 'Descrição', type: 'textarea', placeholder: 'Descrição do centro de custo' },
            { name: 'responsavel', label: 'Responsável', type: 'text', placeholder: 'Nome do responsável' },
          ]}
          onSave={(dados) => apiGenericCreate('centros-custo', dados, carregarCentrosCusto)} />
      )}

      {showModal === 'plano-contas' && (
        <CadastroModal title="Nova Conta" icon={<FileText className="w-5 h-5" />} color="blue" onClose={() => setShowModal(null)}
          fields={[
            { name: 'codigo', label: 'Código *', type: 'text', required: true, placeholder: 'Ex: 1.1.01' },
            { name: 'nome', label: 'Nome *', type: 'text', required: true, placeholder: 'Ex: Caixa' },
            { name: 'tipo', label: 'Tipo', type: 'select', options: [
              { value: 'analitica', label: 'Analítica' }, { value: 'sintetica', label: 'Sintética' }
            ]},
            { name: 'natureza', label: 'Natureza', type: 'select', options: [
              { value: 'despesa', label: 'Despesa' }, { value: 'receita', label: 'Receita' },
              { value: 'ativo', label: 'Ativo' }, { value: 'passivo', label: 'Passivo' }, { value: 'patrimonio', label: 'Patrimônio' }
            ]},
            { name: 'contaPai', label: 'Conta Pai', type: 'text', placeholder: 'Código da conta pai (opcional)' },
            { name: 'descricao', label: 'Descrição', type: 'textarea', placeholder: 'Descrição da conta' },
          ]}
          onSave={(dados) => apiGenericCreate('plano-contas', dados, carregarPlanoContas)} />
      )}

      {showModal === 'orcamentos' && (
        <OrcamentoModal onClose={() => setShowModal(null)}
          onSave={(dados) => apiGenericCreate('orcamentos', dados, carregarOrcamentos)}
          categorias={categorias} centrosCusto={centrosCusto} />
      )}

      {showModal === 'caixas-bancos' && (
        <CaixaBancoModal onClose={() => setShowModal(null)}
          onSave={(dados) => apiGenericCreate('contas-bancarias', dados, carregarContasBancarias)} />
      )}

      {showModal === 'compras' && (
        <CompraModal onClose={() => setShowModal(null)}
          onSave={(dados) => apiGenericCreate('compras', dados, carregarCompras)}
          centrosCusto={centrosCusto} fornecedores={fornecedores} />
      )}

      {showModal === 'fornecedores' && (
        <CadastroModal title="Novo Fornecedor" icon={<Truck className="w-5 h-5" />} color="blue" columns={2} onClose={() => setShowModal(null)}
          fields={[
            { name: 'razaoSocial', label: 'Razão Social *', type: 'text', required: true, placeholder: 'Razão social da empresa' },
            { name: 'nomeFantasia', label: 'Nome Fantasia', type: 'text', placeholder: 'Nome fantasia' },
            { name: 'cnpj', label: 'CNPJ', type: 'text', placeholder: '00.000.000/0000-00' },
            { name: 'email', label: 'E-mail', type: 'text', placeholder: 'email@fornecedor.com' },
            { name: 'telefone', label: 'Telefone', type: 'text', placeholder: '(00) 00000-0000' },
            { name: 'contato', label: 'Pessoa de Contato', type: 'text', placeholder: 'Nome do contato' },
            { name: 'endereco', label: 'Endereço', type: 'text', placeholder: 'Rua, número, bairro' },
            { name: 'cidade', label: 'Cidade', type: 'text', placeholder: 'Cidade' },
            { name: 'estado', label: 'Estado (UF)', type: 'text', placeholder: 'UF' },
            { name: 'observacoes', label: 'Observações', type: 'textarea', placeholder: 'Observações sobre o fornecedor' },
          ]}
          onSave={(dados) => apiGenericCreate('fornecedores', dados, carregarFornecedores)} />
      )}

      {showModal === 'compradores' && (
        <CadastroModal title="Novo Comprador" icon={<UserCheck className="w-5 h-5" />} color="blue" onClose={() => setShowModal(null)}
          fields={[
            { name: 'nome', label: 'Nome *', type: 'text', required: true, placeholder: 'Nome completo' },
            { name: 'email', label: 'E-mail', type: 'text', placeholder: 'email@empresa.com' },
            { name: 'departamento', label: 'Departamento', type: 'text', placeholder: 'Ex: Compras, Administrativo' },
            { name: 'limiteAprovacao', label: 'Limite de Aprovação (R$)', type: 'number', placeholder: '0.00' },
          ]}
          onSave={(dados) => apiGenericCreate('compradores', { ...dados, limiteAprovacao: parseFloat(String(dados.limiteAprovacao) || '0') }, carregarCompradores)} />
      )}

      {showModal === 'produtos' && (
        <CadastroModal title="Novo Produto" icon={<PackageSearch className="w-5 h-5" />} color="blue" onClose={() => setShowModal(null)}
          fields={[
            { name: 'codigo', label: 'Código *', type: 'text', required: true, placeholder: 'Ex: PROD001' },
            { name: 'nome', label: 'Nome *', type: 'text', required: true, placeholder: 'Nome do produto' },
            { name: 'categoria', label: 'Categoria', type: 'text', placeholder: 'Ex: Material de escritório' },
            { name: 'unidade', label: 'Unidade', type: 'select', options: [
              { value: 'UN', label: 'Unidade (UN)' }, { value: 'CX', label: 'Caixa (CX)' },
              { value: 'KG', label: 'Quilograma (KG)' }, { value: 'L', label: 'Litro (L)' },
              { value: 'M', label: 'Metro (M)' }, { value: 'PC', label: 'Peça (PC)' },
              { value: 'PAR', label: 'Par (PAR)' }, { value: 'JG', label: 'Jogo (JG)' },
            ]},
            { name: 'precoCompra', label: 'Preço de Compra (R$)', type: 'number', placeholder: '0.00' },
            { name: 'precoVenda', label: 'Preço de Venda (R$)', type: 'number', placeholder: '0.00' },
            { name: 'descricao', label: 'Descrição', type: 'textarea', placeholder: 'Detalhes do produto' },
          ]}
          onSave={(dados) => apiGenericCreate('produtos', {
            ...dados,
            precoCompra: parseFloat(String(dados.precoCompra) || '0'),
            precoVenda: parseFloat(String(dados.precoVenda) || '0'),
            ativo: true,
          }, carregarProdutos)} />
      )}

      {showModal === 'estoque' && (
        <CadastroModal title="Novo Item de Estoque" icon={<Boxes className="w-5 h-5" />} color="blue" onClose={() => setShowModal(null)}
          fields={[
            { name: 'codigo', label: 'Código *', type: 'text', required: true, placeholder: 'Ex: MAT001' },
            { name: 'nome', label: 'Nome *', type: 'text', required: true, placeholder: 'Nome do item' },
            { name: 'categoria', label: 'Categoria', type: 'text', placeholder: 'Ex: Material de escritório' },
            { name: 'unidade', label: 'Unidade', type: 'select', options: [
              { value: 'UN', label: 'Unidade (UN)' }, { value: 'CX', label: 'Caixa (CX)' }, 
              { value: 'KG', label: 'Quilograma (KG)' }, { value: 'L', label: 'Litro (L)' },
              { value: 'M', label: 'Metro (M)' }, { value: 'PC', label: 'Peça (PC)' },
              { value: 'PAR', label: 'Par (PAR)' }, { value: 'JG', label: 'Jogo (JG)' },
            ]},
            { name: 'quantidadeAtual', label: 'Quantidade Atual', type: 'number', placeholder: '0' },
            { name: 'quantidadeMinima', label: 'Quantidade Mínima', type: 'number', placeholder: '0' },
            { name: 'localizacao', label: 'Localização', type: 'text', placeholder: 'Ex: Almoxarifado A, Prateleira 3' },
            { name: 'custoMedio', label: 'Custo Médio (R$)', type: 'number', placeholder: '0.00' },
          ]}
          onSave={(dados) => apiGenericCreate('estoque', { 
            ...dados, 
            quantidadeAtual: parseFloat(String(dados.quantidadeAtual) || '0'),
            quantidadeMinima: parseFloat(String(dados.quantidadeMinima) || '0'),
            custoMedio: parseFloat(String(dados.custoMedio) || '0'),
          }, carregarEstoque)} />
      )}

      {showModal === 'inventario' && (
        <CadastroModal title="Novo Inventário" icon={<ClipboardList className="w-5 h-5" />} color="blue" onClose={() => setShowModal(null)}
          fields={[
            { name: 'descricao', label: 'Descrição *', type: 'text', required: true, placeholder: 'Ex: Inventário Mensal Março/2026' },
            { name: 'responsavel', label: 'Responsável *', type: 'text', required: true, placeholder: 'Nome do responsável' },
            { name: 'dataInicio', label: 'Data Início', type: 'date' },
            { name: 'dataFim', label: 'Data Fim (Previsão)', type: 'date' },
            { name: 'observacoes', label: 'Observações', type: 'textarea', placeholder: 'Informações adicionais' },
          ]}
          onSave={(dados) => apiGenericCreate('inventarios', dados, carregarInventarios)} />
      )}

      {!!detalhando && (
        <DetalheLancamento
          conta={detalhando}
          moedaBase={config.moedaBase}
          userEmail={session.usuario.email}
          onFechar={() => setDetalhando(null)}
          onBaixar={() => { setBaixando(detalhando); setDetalhando(null); }}
          onExcluir={() => { const c = detalhando; setDetalhando(null); deletarConta(c); }}
          onEmitirCobranca={detalhando.tipo === 'receber'
            ? () => { setCobrando(detalhando); setDetalhando(null); }
            : undefined} />
      )}

      {!!cobrando && (
        <CobrancaModal
          conta={cobrando}
          emitentePadrao={config.emitente}
          bancoPadrao={config.dadosBancarios}
          clientesConhecidos={clientesCobranca}
          moedaBase={config.moedaBase}
          moedas={config.moedas || [config.moedaBase]}
          onFechar={() => setCobrando(null)}
          onEmitir={emitirCobranca} />
      )}
    </div>
  );
};


/* ═══════════════════════════════════════════════════════════
   MODAL GENÉRICO DE CADASTRO
   ═══════════════════════════════════════════════════════════ */

interface FieldDef {
  name: string;
  label: string;
  type: 'text' | 'textarea' | 'select' | 'color' | 'number' | 'date';
  required?: boolean;
  placeholder?: string;
  defaultValue?: string;
  options?: Array<{ value: string; label: string }>;
  half?: boolean;
}

const CadastroModal: React.FC<{
  title: string;
  icon: React.ReactNode;
  color: string;
  fields: FieldDef[];
  onSave: (dados: Record<string, unknown>) => void;
  onClose: () => void;
  columns?: number;
}> = ({ title, icon, fields, onSave, onClose, columns }) => {
  const [form, setForm] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    fields.forEach(f => { initial[f.name] = f.defaultValue || (f.type === 'select' && f.options ? f.options[0].value : ''); });
    return initial;
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const required = fields.filter(f => f.required);
    if (required.some(f => !form[f.name])) return;
    onSave(form);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm overflow-y-auto py-8">
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${columns === 2 ? 'max-w-2xl' : 'max-w-lg'} mx-4 overflow-hidden`}>
        <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-lg flex items-center gap-2">{icon} {title}</h3>
            <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-lg transition-colors"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="p-6 max-h-[70vh] overflow-y-auto">
          <div className={columns === 2 ? 'grid grid-cols-2 gap-4' : 'space-y-4'}>
          {fields.map(field => (
            <div key={field.name} className={field.type === 'textarea' && columns === 2 ? 'col-span-2' : ''}>
              <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
              {field.type === 'textarea' ? (
                <textarea value={form[field.name] || ''} onChange={e => setForm(prev => ({ ...prev, [field.name]: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 resize-none"
                  rows={3} placeholder={field.placeholder} required={field.required} />
              ) : field.type === 'select' ? (
                <select value={form[field.name] || ''} onChange={e => setForm(prev => ({ ...prev, [field.name]: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400">
                  {field.options?.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
              ) : field.type === 'color' ? (
                <div className="flex items-center gap-3">
                  <input type="color" value={form[field.name] || '#3b82f6'} onChange={e => setForm(prev => ({ ...prev, [field.name]: e.target.value }))}
                    className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer" />
                  <span className="text-sm text-gray-500">{form[field.name] || '#3b82f6'}</span>
                </div>
              ) : (
                <input type={field.type || 'text'} value={form[field.name] || ''} onChange={e => setForm(prev => ({ ...prev, [field.name]: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                  placeholder={field.placeholder} required={field.required} />
              )}
            </div>
          ))}
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">Cancelar</button>
            <button type="submit" className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:shadow-lg transition-all">Salvar</button>
          </div>
        </form>
      </div>
    </div>
  );
};


/* ═══════════════════════════════════════════════════════════
   MODAL NOVA CONTA (PAGAR/RECEBER)
   ═══════════════════════════════════════════════════════════ */

/* ═══════════════════════════════════════════════════════════
   BAIXA DE TÍTULO
   Pagar/receber sem dizer por onde o dinheiro andou deixa o
   saldo do caixa/banco errado — então a baixa passa por aqui.
   ═══════════════════════════════════════════════════════════ */
const BaixaModal: React.FC<{
  conta: ContaFinanceira;
  config: FinanceConfig;
  fontes: Fonte[];
  onConfirm: (extra: Record<string, unknown>) => void;
  onClose: () => void;
}> = ({ conta, config, fontes, onConfirm, onClose }) => {
  const hoje = new Date().toISOString().split('T')[0];
  const ehPagar = conta.tipo === 'pagar';
  const [fonteId, setFonteId] = useState(conta.fonteId || '');
  const [data, setData] = useState(hoje);

  const disponiveis = fontes.filter(f => f.ativo !== false || f._id === conta.fonteId);
  const fonteSel = disponiveis.find(f => f._id === fonteId);
  const moedaConta = conta.moeda || config.moedaBase;
  const divergente = !!fonteSel && fonteSel.moeda !== moedaConta;

  const confirmar = (e: React.FormEvent) => {
    e.preventDefault();
    if (!fonteId) { alert(ehPagar ? 'Diga de qual caixa/banco saiu o dinheiro.' : 'Diga em qual caixa/banco o dinheiro entrou.'); return; }
    onConfirm(ehPagar
      ? { fonteId, dataPagamento: data }
      : { fonteId, dataRecebimento: data });
  };

  const campo = 'w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm overflow-y-auto py-8">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className={`px-6 py-4 ${ehPagar ? 'bg-gradient-to-r from-red-500 to-rose-600' : 'bg-gradient-to-r from-green-500 to-emerald-600'} text-white`}>
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-lg">{ehPagar ? 'Registrar pagamento' : 'Registrar recebimento'}</h3>
            <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-lg transition-colors"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <form onSubmit={confirmar} className="p-6 space-y-4">
          <div className="px-4 py-3 rounded-xl bg-gray-50 border border-gray-100">
            <p className="text-sm font-medium text-gray-900">{conta.descricao}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {conta.fornecedor || conta.cliente || 'sem contraparte'} · vence {conta.vencimento?.slice(0, 10).split('-').reverse().join('/')}
            </p>
            <p className={`text-lg font-bold mt-1 ${ehPagar ? 'text-red-600' : 'text-green-600'}`}>
              {fmt(conta.valor, moedaConta)}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {ehPagar ? 'Por onde eu paguei *' : 'Onde o dinheiro entrou *'}
            </label>
            <select value={fonteId} onChange={e => setFonteId(e.target.value)} className={campo} required>
              <option value="">Selecione o caixa / banco…</option>
              {disponiveis.map(f => (
                <option key={f._id} value={f._id}>{f.nome} ({f.moeda})</option>
              ))}
            </select>
            {!disponiveis.length && (
              <p className="text-[11px] text-amber-600 mt-1">
                Nenhuma fonte cadastrada — crie uma em "Fontes de Recurso" antes de dar baixa.
              </p>
            )}
            {divergente && (
              <p className="text-[11px] text-amber-600 mt-1">
                A fonte "{fonteSel?.nome}" é em {fonteSel?.moeda} e o título está em {moedaConta}. O saldo dela vai somar o valor em {moedaConta}.
              </p>
            )}
            {fonteSel?.reembolsavelPadrao && ehPagar && (
              <p className="text-[11px] text-gray-500 mt-1">
                "{fonteSel.nome}" é fonte reembolsável — esse gasto continua na lista de reembolsos a receber.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {ehPagar ? 'Data do pagamento' : 'Data do recebimento'}
            </label>
            <input type="date" value={data} onChange={e => setData(e.target.value)} className={campo} required />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-600 border border-gray-200 hover:bg-gray-50">
              Cancelar
            </button>
            <button type="submit" disabled={!disponiveis.length}
              className={`px-5 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50 ${ehPagar ? 'bg-gradient-to-r from-red-500 to-rose-600' : 'bg-gradient-to-r from-green-500 to-emerald-600'} hover:shadow-lg`}>
              {ehPagar ? 'Confirmar pagamento' : 'Confirmar recebimento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const NovaContaModal: React.FC<{
  tipo: 'pagar' | 'receber';
  config: FinanceConfig;
  fontes: Fonte[];
  categorias: string[];
  userEmail: string;
  onSave: (dados: Record<string, unknown>) => void;
  onClose: () => void;
}> = ({ tipo, config, fontes, categorias, userEmail, onSave, onClose }) => {
  const hoje = new Date().toISOString().split('T')[0];
  const [descricao, setDescricao] = useState('');
  const [valor, setValor] = useState('');
  const [moeda, setMoeda] = useState(config.moedaBase);
  const [cotacao, setCotacao] = useState('');
  const [vencimento, setVencimento] = useState(hoje);
  const [categoria, setCategoria] = useState('');
  const [entidade, setEntidade] = useState('');
  const [fonteId, setFonteId] = useState('');
  const [reembolsavel, setReembolsavel] = useState(false);
  const [tocouReembolso, setTocouReembolso] = useState(false);
  const [jaLiquidado, setJaLiquidado] = useState(false);
  const [dataLiquidacao, setDataLiquidacao] = useState(hoje);
  const [observacoes, setObservacoes] = useState('');
  const [anexos, setAnexos] = useState<Anexo[]>([]);
  const [subindo, setSubindo] = useState(false);

  const ehBase = moeda === config.moedaBase;
  const cotacaoEfetiva = ehBase ? 1 : (parseFloat(cotacao) || config.cotacoes[moeda] || 0);
  const valorNum = parseFloat(valor) || 0;
  const equivalente = valorNum * cotacaoEfetiva;
  const fonteSel = fontes.find(f => f._id === fonteId);

  // Trocar a fonte reaplica o padrão dela — a menos que você já tenha decidido na mão.
  const trocarFonte = (id: string) => {
    setFonteId(id);
    if (!tocouReembolso) {
      const f = fontes.find(x => x._id === id);
      setReembolsavel(!!f?.reembolsavelPadrao);
      if (f?.moeda) setMoeda(f.moeda);
    }
  };

  const anexar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setSubindo(true);
    for (const f of files) {
      const anexo = await uploadAnexo(f, descricao ? descricao.slice(0, 60) : 'Comprovantes', userEmail);
      if (anexo) setAnexos(a => [...a, anexo]);
      else alert(`Não consegui subir "${f.name}".`);
    }
    setSubindo(false);
    e.target.value = '';
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!descricao || !valor || !vencimento) return;
    if (!ehBase && cotacaoEfetiva <= 0) {
      alert(`Informe a cotação: quanto vale 1 ${moeda} em ${config.moedaBase}.`);
      return;
    }
    // já liquidado sem fonte = saldo de caixa/banco errado
    if (jaLiquidado && !fonteId) {
      alert(tipo === 'pagar'
        ? 'Diga de qual caixa/banco saiu o dinheiro para marcar como pago.'
        : 'Diga em qual caixa/banco o dinheiro entrou para marcar como recebido.');
      return;
    }
    const dados: Record<string, unknown> = {
      descricao, valor: valorNum, moeda, cotacao: cotacaoEfetiva,
      vencimento, categoria, observacoes, fonteId, anexos,
    };
    if (tipo === 'pagar') {
      dados.fornecedor = entidade;
      dados.reembolsavel = reembolsavel;
      if (jaLiquidado) { dados.status = 'pago'; dados.dataPagamento = dataLiquidacao; }
    } else {
      dados.cliente = entidade;
      if (jaLiquidado) { dados.status = 'recebido'; dados.dataRecebimento = dataLiquidacao; }
    }
    onSave(dados);
  };

  const campo = 'w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm overflow-y-auto py-8">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
        <div className={`px-6 py-4 ${tipo === 'pagar' ? 'bg-gradient-to-r from-red-500 to-rose-600' : 'bg-gradient-to-r from-green-500 to-emerald-600'} text-white`}>
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-lg">{tipo === 'pagar' ? '📉 Nova Conta a Pagar' : '📈 Nova Conta a Receber'}</h3>
            <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-lg transition-colors"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descrição *</label>
            <input type="text" value={descricao} onChange={e => setDescricao(e.target.value)}
              className={campo} placeholder={tipo === 'pagar' ? 'Ex: Passagem para o evento em Munique' : 'Ex: Parcela do patrocínio'} required />
          </div>

          {/* ─── Valor + moeda + cotação ─── */}
          <div className="grid grid-cols-4 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Moeda</label>
              <select value={moeda} onChange={e => setMoeda(e.target.value)} className={campo}>
                {config.moedas.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valor *</label>
              <input type="number" step="0.01" min="0" value={valor} onChange={e => setValor(e.target.value)}
                className={campo} placeholder="0,00" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {ehBase ? 'Cotação' : `1 ${moeda} = ? ${config.moedaBase}`}
              </label>
              <input type="number" step="0.0001" disabled={ehBase} value={ehBase ? 1 : cotacao}
                onChange={e => setCotacao(e.target.value)}
                className={`${campo} ${ehBase ? 'bg-gray-100 text-gray-400' : ''}`}
                placeholder={String(config.cotacoes[moeda] || '')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vencimento *</label>
              <input type="date" value={vencimento} onChange={e => setVencimento(e.target.value)}
                className={campo} required />
            </div>
          </div>
          {!ehBase && valorNum > 0 && (
            <p className="text-xs text-gray-500 -mt-2">
              {cotacaoEfetiva > 0
                ? <>Equivale a <strong>{fmt(equivalente, config.moedaBase)}</strong>.</>
                : <span className="text-amber-600">Informe a cotação para converter em {config.moedaBase}.</span>}
            </p>
          )}

          {/* ─── De onde sai / para onde entra ─── */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {tipo === 'pagar' ? 'De onde saiu o dinheiro' : 'Para onde o dinheiro entra'}
              </label>
              <select value={fonteId} onChange={e => trocarFonte(e.target.value)} className={campo}>
                <option value="">Não informado</option>
                {fontes.filter(f => f.ativo !== false).map(f => (
                  <option key={f._id} value={f._id}>{f.nome} ({f.moeda})</option>
                ))}
              </select>
              {!fontes.length && (
                <p className="text-[11px] text-amber-600 mt-1">
                  Nenhuma fonte cadastrada ainda — crie em "Fontes de Recurso".
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{tipo === 'pagar' ? 'Fornecedor' : 'Cliente / Pagador'}</label>
              <input type="text" value={entidade} onChange={e => setEntidade(e.target.value)}
                className={campo} placeholder={tipo === 'pagar' ? 'Quem recebeu' : 'Quem vai pagar'} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Categoria</label>
            <input type="text" list="cats-conta" value={categoria} onChange={e => setCategoria(e.target.value)}
              className={campo} placeholder="Ex: Viagem" />
            <datalist id="cats-conta">
              {categorias.map(c => <option key={c} value={c} />)}
            </datalist>
          </div>

          {tipo === 'pagar' && (
            <label className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 cursor-pointer hover:bg-gray-50">
              <input type="checkbox" checked={reembolsavel} className="mt-0.5"
                onChange={e => { setReembolsavel(e.target.checked); setTocouReembolso(true); }} />
              <span className="text-sm">
                <span className="font-medium text-gray-800">Esse dinheiro precisa voltar para mim</span>
                <span className="block text-xs text-gray-500">
                  {fonteSel?.reembolsavelPadrao
                    ? `Marcado automaticamente porque "${fonteSel.nome}" é uma fonte reembolsável.`
                    : 'Marque quando você adiantou do próprio bolso e será ressarcida depois.'}
                </span>
              </span>
            </label>
          )}

          <label className="flex items-center gap-3 text-sm text-gray-700">
            <input type="checkbox" checked={jaLiquidado} onChange={e => setJaLiquidado(e.target.checked)} />
            {tipo === 'pagar' ? 'Já paguei' : 'Já recebi'}
            {jaLiquidado && (
              <input type="date" value={dataLiquidacao} onChange={e => setDataLiquidacao(e.target.value)}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm" />
            )}
          </label>

          {/* ─── Anexos ─── */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">Comprovantes / contrato</label>
              <label className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1 cursor-pointer">
                <Paperclip className="w-3 h-3" /> {subindo ? 'Enviando…' : 'Anexar'}
                <input type="file" multiple className="hidden" onChange={anexar} disabled={subindo} />
              </label>
            </div>
            {anexos.map((a, i) => (
              <div key={a.url || i} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-xl border border-gray-100 mb-1">
                <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                <a href={linkAnexo(a.url, userEmail)} target="_blank" rel="noreferrer" className="flex-1 text-sm text-blue-700 hover:underline truncate">{a.nome}</a>
                <button type="button" onClick={() => setAnexos(x => x.filter((_, idx) => idx !== i))}
                  className="p-1 text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
            <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)}
              className={`${campo} resize-none`} rows={2} placeholder="Informações adicionais..." />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">Cancelar</button>
            <button type="submit" className={`px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all hover:shadow-lg
              ${tipo === 'pagar' ? 'bg-gradient-to-r from-red-500 to-rose-600' : 'bg-gradient-to-r from-green-500 to-emerald-600'}`}>Salvar</button>
          </div>
        </form>
      </div>
    </div>
  );
};


/* ═══════════════════════════════════════════════════════════
   MODAL NOVA LINHA ORÇAMENTÁRIA (BUDGET)
   ═══════════════════════════════════════════════════════════ */

const OrcamentoModal: React.FC<{
  onSave: (dados: Record<string, unknown>) => void;
  onClose: () => void;
  categorias: Categoria[];
  centrosCusto: CentroCusto[];
}> = ({ onSave, onClose, categorias, centrosCusto }) => {
  const [categoria, setCategoria] = useState('');
  const [centroCusto, setCentroCusto] = useState('');
  const [ano, setAno] = useState(new Date().getFullYear());
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [valorOrcado, setValorOrcado] = useState<number>(0);
  const [valorRealizado, setValorRealizado] = useState<number>(0);
  const [observacoes, setObservacoes] = useState('');

  const meses = [
    { value: 1, label: 'Janeiro' }, { value: 2, label: 'Fevereiro' }, { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' }, { value: 5, label: 'Maio' }, { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' }, { value: 11, label: 'Novembro' }, { value: 12, label: 'Dezembro' }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!categoria) return;
    onSave({ categoria, centroCusto, ano, mes, periodo: `${mes}/${ano}`, valorOrcado, valorRealizado, observacoes });
  };

  const inputCls = 'w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm overflow-y-auto py-8">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-lg flex items-center gap-2"><Calculator className="w-5 h-5" /> Nova Linha Orçamentária</h3>
            <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-lg transition-colors"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Categoria *</label>
            <select value={categoria} onChange={e => setCategoria(e.target.value)} className={inputCls} required>
              <option value="">Selecione...</option>
              {categorias.map(c => <option key={c._id} value={c.nome}>{c.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Centro de Custo</label>
            <select value={centroCusto} onChange={e => setCentroCusto(e.target.value)} className={inputCls}>
              <option value="">Selecione...</option>
              {centrosCusto.map(c => <option key={c._id} value={c.nome}>{c.nome}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ano *</label>
              <input type="number" value={ano} onChange={e => setAno(parseInt(e.target.value) || new Date().getFullYear())}
                className={inputCls} min={2020} max={2050} required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mês *</label>
              <select value={mes} onChange={e => setMes(parseInt(e.target.value))} className={inputCls} required>
                {meses.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valor Orçado (R$) *</label>
              <input type="number" step="0.01" min="0" value={valorOrcado || ''} onChange={e => setValorOrcado(parseFloat(e.target.value) || 0)}
                className={inputCls} placeholder="0,00" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valor Realizado (R$)</label>
              <input type="number" step="0.01" min="0" value={valorRealizado || ''} onChange={e => setValorRealizado(parseFloat(e.target.value) || 0)}
                className={inputCls} placeholder="0,00" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
            <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)}
              className={`${inputCls} resize-none`} rows={2} placeholder="Informações adicionais..." />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">Cancelar</button>
            <button type="submit" className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:shadow-lg transition-all">Salvar</button>
          </div>
        </form>
      </div>
    </div>
  );
};


/* ═══════════════════════════════════════════════════════════
   MODAL NOVA CONTA BANCÁRIA
   ═══════════════════════════════════════════════════════════ */

const CaixaBancoModal: React.FC<{
  onSave: (dados: Record<string, unknown>) => void;
  onClose: () => void;
}> = ({ onSave, onClose }) => {
  const [banco, setBanco] = useState('');
  const [apelido, setApelido] = useState('');
  const [agencia, setAgencia] = useState('');
  const [conta, setConta] = useState('');
  const [tipo, setTipo] = useState<string>('corrente');
  const [saldoInicial, setSaldoInicial] = useState<number>(0);
  const [observacoes, setObservacoes] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!banco) return;
    onSave({ banco, apelido, agencia, conta, tipo, saldoInicial, saldoAtual: saldoInicial, ativo: true, observacoes });
  };

  const inputCls = 'w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm overflow-y-auto py-8">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-lg flex items-center gap-2"><Landmark className="w-5 h-5" /> Nova Conta Bancária</h3>
            <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-lg transition-colors"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Banco *</label>
              <input type="text" value={banco} onChange={e => setBanco(e.target.value)}
                className={inputCls} placeholder="Ex: Banco do Brasil, Itaú..." required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Apelido</label>
              <input type="text" value={apelido} onChange={e => setApelido(e.target.value)}
                className={inputCls} placeholder="Ex: Conta Principal" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Agência</label>
              <input type="text" value={agencia} onChange={e => setAgencia(e.target.value)}
                className={inputCls} placeholder="0000" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Conta</label>
              <input type="text" value={conta} onChange={e => setConta(e.target.value)}
                className={inputCls} placeholder="00000-0" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo *</label>
              <select value={tipo} onChange={e => setTipo(e.target.value)} className={inputCls} required>
                <option value="corrente">Corrente</option>
                <option value="poupanca">Poupança</option>
                <option value="investimento">Investimento</option>
                <option value="caixa">Caixa</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Saldo Inicial (R$)</label>
            <input type="number" step="0.01" value={saldoInicial || ''} onChange={e => setSaldoInicial(parseFloat(e.target.value) || 0)}
              className={inputCls} placeholder="0,00" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
            <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)}
              className={`${inputCls} resize-none`} rows={2} placeholder="Informações adicionais..." />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">Cancelar</button>
            <button type="submit" className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:shadow-lg transition-all">Salvar</button>
          </div>
        </form>
      </div>
    </div>
  );
};


/* ═══════════════════════════════════════════════════════════
   MODAL NOVA COMPRA
   ═══════════════════════════════════════════════════════════ */

const CompraModal: React.FC<{
  onSave: (dados: Record<string, unknown>) => void;
  onClose: () => void;
  centrosCusto: CentroCusto[];
  fornecedores: Fornecedor[];
}> = ({ onSave, onClose, centrosCusto, fornecedores }) => {
  const [fornecedor, setFornecedor] = useState('');
  const [descricao, setDescricao] = useState('');
  const [dataCompra, setDataCompra] = useState(new Date().toISOString().split('T')[0]);
  const [dataEntrega, setDataEntrega] = useState('');
  const [formaPagamento, setFormaPagamento] = useState('');
  const [centroCusto, setCentroCusto] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [itens, setItens] = useState<Array<{ descricao: string; quantidade: number; valorUnitario: number }>>([
    { descricao: '', quantidade: 1, valorUnitario: 0 }
  ]);

  const adicionarItem = () => setItens(prev => [...prev, { descricao: '', quantidade: 1, valorUnitario: 0 }]);
  const removerItem = (i: number) => setItens(prev => prev.filter((_, idx) => idx !== i));
  const atualizarItem = (i: number, field: string, value: string | number) => {
    setItens(prev => prev.map((item, idx) => idx === i ? { ...item, [field]: value } : item));
  };

  const valorTotal = itens.reduce((sum, item) => sum + (item.quantidade * item.valorUnitario), 0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!fornecedor) return;
    onSave({
      fornecedor, descricao, dataCompra, dataEntrega, formaPagamento, centroCusto, observacoes,
      itens: itens.filter(i => i.descricao).map(i => ({ ...i, total: i.quantidade * i.valorUnitario })),
      valorTotal
    });
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm overflow-y-auto py-8">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-lg flex items-center gap-2"><ShoppingCart className="w-5 h-5" /> Nova Compra</h3>
            <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-lg transition-colors"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fornecedor *</label>
              <select value={fornecedor} onChange={e => setFornecedor(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" required>
                <option value="">Selecione o fornecedor</option>
                {fornecedores.filter(f => f.ativo !== false).map(f => (
                  <option key={f._id} value={f.nomeFantasia || f.razaoSocial}>{f.nomeFantasia || f.razaoSocial}{f.cnpj ? ` (${f.cnpj})` : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Forma de Pagamento</label>
              <select value={formaPagamento} onChange={e => setFormaPagamento(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400">
                <option value="">Selecione</option>
                <option value="pix">PIX</option>
                <option value="boleto">Boleto</option>
                <option value="cartao">Cartão</option>
                <option value="transferencia">Transferência</option>
                <option value="dinheiro">Dinheiro</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
            <input type="text" value={descricao} onChange={e => setDescricao(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
              placeholder="Descrição do pedido de compra" />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data da Compra</label>
              <input type="date" value={dataCompra} onChange={e => setDataCompra(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Previsão Entrega</label>
              <input type="date" value={dataEntrega} onChange={e => setDataEntrega(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Centro de Custo</label>
              <select value={centroCusto} onChange={e => setCentroCusto(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400">
                <option value="">Nenhum</option>
                {centrosCusto.filter(c => c.ativo).map(cc => <option key={cc._id} value={cc.nome}>{cc.codigo} - {cc.nome}</option>)}
              </select>
            </div>
          </div>

          {/* Itens */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">Itens</label>
              <button type="button" onClick={adicionarItem} className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1">
                <Plus className="w-3 h-3" /> Adicionar item
              </button>
            </div>
            <div className="space-y-2">
              {itens.map((item, i) => (
                <div key={i} className="flex items-center gap-2 p-3 bg-gray-50 rounded-xl border border-gray-100">
                  <input type="text" value={item.descricao} onChange={e => atualizarItem(i, 'descricao', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    placeholder="Descrição do item" />
                  <input type="number" min="1" value={item.quantidade} onChange={e => atualizarItem(i, 'quantidade', parseInt(e.target.value) || 1)}
                    className="w-20 px-3 py-2 border border-gray-200 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    placeholder="Qtd" />
                  <input type="number" step="0.01" min="0" value={item.valorUnitario || ''} onChange={e => atualizarItem(i, 'valorUnitario', parseFloat(e.target.value) || 0)}
                    className="w-28 px-3 py-2 border border-gray-200 rounded-lg text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                    placeholder="R$ Unit." />
                  <span className="w-28 text-sm font-semibold text-right text-blue-700">
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.quantidade * item.valorUnitario)}
                  </span>
                  {itens.length > 1 && (
                    <button type="button" onClick={() => removerItem(i)} className="p-1 text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-3 px-3">
              <span className="text-lg font-bold text-blue-800">
                Total: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valorTotal)}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
            <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 resize-none"
              rows={2} placeholder="Informações adicionais..." />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">Cancelar</button>
            <button type="submit" className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:shadow-lg transition-all">Salvar Compra</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FinanceApp;
